import React, { useState, useEffect, useRef, Fragment } from 'react';
import { api } from '../api/client';
import { useT, useFormatDate } from '../i18n';
import { TabBar } from '../components/TabBar';
import { MetricCard } from '../components/MetricCard';
import { AttendanceHeatmap } from '../components/AttendanceHeatmap';
import { AIPanel } from '../components/AIPanel';
import { WeeklyChart } from '../components/WeeklyChart';
import { Sparkline } from '../components/Sparkline';
import { ProgressRing } from '../components/ProgressRing';
import { DocumentCard, type DocumentSummary } from '../components/DocumentCard';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { AdjustmentModal } from '../components/AdjustmentModal';
import { CommunicationsPanel } from '../components/CommunicationsPanel';
import { AdjustmentHistory } from '../components/AdjustmentHistory';
import { AppointmentDetailModal, type AppointmentDetail } from '../components/AppointmentDetailModal';
import { MeasureTimeline, type OaiTimelineData } from '../components/MeasureTimeline';
import { RiTimeline, type RiTimelineData } from '../components/RiTimeline';


const BASE_TABS: { key: string; labelKey: string }[] = [
  { key: 'overview', labelKey: 'drilldown.tab.overview' },
  { key: 'measures', labelKey: 'drilldown.tab.measures' },
  { key: 'attendance', labelKey: 'drilldown.tab.attendance' },
  { key: 'journal', labelKey: 'drilldown.tab.journal' },
  { key: 'timeline', labelKey: 'drilldown.tab.timeline' },
  { key: 'communications', labelKey: 'drilldown.tab.communications' },
];

function cleanHtml(html: string | null): string {
  if (!html) return '';
  // Strip HTML tags for plain text display
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return d.slice(0, 10);
}

/** Format hours: "5 h" or "1,5 h" — no decimal if whole number, comma for decimal, space before h */
function fmtH(h: number): string {
  if (h % 1 === 0) return `${h} h`;
  return `${h.toFixed(1).replace('.', ',')} h`;
}

/** Hover tooltip — shows a styled popup with explanation text, flips left when near right edge */
function InfoTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const [flipLeft, setFlipLeft] = useState(false);
  const ref = React.useRef<HTMLSpanElement>(null);

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setFlipLeft(rect.left + 300 > window.innerWidth);
    }
    setShow(true);
  };

  return (
    <span
      ref={ref}
      className="infotip"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      ?
      {show && <div className={`infotip__popup${flipLeft ? ' infotip__popup--left' : ''}`}>{text}</div>}
    </span>
  );
}

const ABSENCE_TYPE_KEYS = ['vacation', 'illness', 'accident', 'measure_suspended'];


/** PlannedVsAchievedTrend — the Prévu/Réalisé KPI visual, in exactly the
 *  FourWeekTrend style and geometry (same w/h/plotH so the KPI row keeps a
 *  uniform rhythm): two lines — prévu dashed orange (hollow dots), réalisé
 *  solid (filled dots) — with the realized value printed at every point
 *  (FourWeekTrend prints its value per point too) and both values + hours
 *  on hover. */
function PlannedVsAchievedTrend({ weeks }: { weeks: Array<{ planned: number; achieved: number; planned_h: number; achieved_h: number }> }) {
  const { t } = useT();
  if (weeks.length < 1) return null;

  const w = 200, padX = 16, topPad = 4, plotH = 30, labelH = 16;
  const h = topPad + plotH + labelH;
  const plotW = w - padX * 2;
  const pts = weeks.slice(-4);
  const max = Math.max(...pts.map(x => Math.max(x.planned, x.achieved)), 10);
  const min = 0;
  const range = max - min || 1;
  const xOf = (i: number) => padX + (pts.length === 1 ? plotW / 2 : (i / (pts.length - 1)) * plotW);
  const yOf = (v: number) => topPad + plotH - ((v - min) / range) * plotH;
  const plannedPts = pts.map((x, i) => ({ x: xOf(i), y: yOf(x.planned), ...x }));
  const achievedPts = pts.map((x, i) => ({ x: xOf(i), y: yOf(x.achieved), ...x }));
  const meetsLast = pts[pts.length - 1].achieved >= pts[pts.length - 1].planned;
  const achievedColor = meetsLast ? 'var(--ok)' : 'var(--accent)';

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', maxWidth: w, marginTop: 6 }}>
      {/* prévu — dashed orange line, hollow dots */}
      <polyline
        points={plannedPts.map(c => `${c.x},${c.y}`).join(' ')}
        fill="none" stroke="var(--warn)" strokeWidth={1.5} strokeDasharray="5 4" strokeLinecap="round" opacity={0.9} />
      {/* réalisé — solid line */}
      <polyline
        points={achievedPts.map(c => `${c.x},${c.y}`).join(' ')}
        fill="none" stroke={achievedColor} strokeWidth={2} strokeLinecap="round" />
      {plannedPts.map((c, i) => (
        <circle key={`p${i}`} cx={c.x} cy={c.y} r={3} fill="var(--surface)" stroke="var(--warn)" strokeWidth={1.5} />
      ))}
      {achievedPts.map((c, i) => (
        <circle key={`a${i}`} cx={c.x} cy={c.y} r={3.5}
          fill={i === pts.length - 1 ? achievedColor : 'var(--surface)'}
          stroke={achievedColor} strokeWidth={1.5} />
      ))}
      {/* per-point label: realized value, exactly like FourWeekTrend prints */}
      {achievedPts.map((c, i) => (
        <text key={`t${i}`} x={c.x} y={topPad + plotH + 12} textAnchor="middle"
              fontSize="10" fontWeight={i === pts.length - 1 ? '600' : '400'}
              fill={i === pts.length - 1 ? 'var(--ink-1)' : 'var(--ink-3)'}>
          {c.achieved}
        </text>
      ))}
      {/* full values on hover */}
      {pts.map((x, i) => (
        <rect key={`h${i}`} x={xOf(i) - plotW / (pts.length * 2)} y={topPad} width={plotW / pts.length} height={plotH + labelH}
              fill="transparent" onMouseEnter={() => undefined}>
          <title>{t('drilldown.kpi.planVsReal.weekTip', {
            planned: x.planned, achieved: x.achieved,
            planned_h: x.planned_h.toFixed(1).replace('.', ','), achieved_h: x.achieved_h.toFixed(1).replace('.', ','),
          })}</title>
        </rect>
      ))}
      <line x1={padX} y1={topPad + plotH} x2={w - padX} y2={topPad + plotH} stroke="var(--line)" strokeWidth={1} />
    </svg>
  );
}

/** FourWeekTrend — 4 dots with connecting lines, accent-shaded area.
 *  Absence weeks shown as squares with dashed line segment. */
function FourWeekTrend({ values, unit = '', formatVal, vacations, absenceTypes }: {
  values: number[];
  unit?: string;
  formatVal?: (v: number) => string;
  vacations?: boolean[];
  absenceTypes?: (string | null)[];
}) {
  const { t } = useT();
  const [hovered, setHovered] = useState<number | null>(null);
  if (values.length < 2) return null;
  const absenceShort = (type: string | null): string =>
    t(`drilldown.absence.short.${ABSENCE_TYPE_KEYS.includes(type || '') ? type : 'default'}`);
  const absenceFull = (type: string | null): string =>
    t(`drilldown.absence.full.${ABSENCE_TYPE_KEYS.includes(type || '') ? type : 'default'}`);
  const fmt = formatVal || ((v: number) => v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', ','));
  const pts = values.slice(-4);
  const vacs = vacations ? vacations.slice(-4) : pts.map(() => false);
  const absTypes = absenceTypes ? absenceTypes.slice(-4) : pts.map(() => null);
  const max = Math.max(...pts, 1);
  const min = Math.min(...pts, 0);
  const range = max - min || 1;

  const w = 200;
  const labelH = 16;
  const plotH = 30;
  const topPad = 4;
  const h = topPad + plotH + labelH;
  const padX = 16;
  const plotW = w - padX * 2;

  const coords = pts.map((v, i) => ({
    x: padX + (pts.length === 1 ? plotW / 2 : (i / (pts.length - 1)) * plotW),
    y: topPad + plotH - ((v - min) / range) * plotH,
    v,
    isVac: vacs[i],
  }));

  // Trend based on non-vacation points only
  const realPts = coords.filter(c => !c.isVac);
  const trending = realPts.length >= 2 ? realPts[realPts.length - 1].v - realPts[0].v : 0;
  const lineColor = Math.abs(trending) < range * 0.05 ? 'var(--accent)' : trending > 0 ? 'var(--ok)' : 'var(--err)';
  const gradId = `fwt-${Math.random().toString(36).slice(2, 6)}`;

  const areaPath = `M${coords[0].x},${topPad + plotH} ` +
    coords.map(c => `L${c.x},${c.y}`).join(' ') +
    ` L${coords[coords.length - 1].x},${topPad + plotH} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', maxWidth: w, marginTop: 6 }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.20" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      {/* Line segments — dashed for vacation transitions */}
      {coords.map((c, i) => {
        if (i === 0) return null;
        const prev = coords[i - 1];
        const isDashed = c.isVac || prev.isVac;
        return (
          <line key={`l${i}`} x1={prev.x} y1={prev.y} x2={c.x} y2={c.y}
            stroke={isDashed ? 'var(--ink-3)' : lineColor} strokeWidth={isDashed ? 1.5 : 2}
            strokeDasharray={isDashed ? '4 3' : 'none'}
            strokeLinecap="round" />
        );
      })}
      {/* Points + labels */}
      {coords.map((c, i) => (
        <g key={i}
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(null)}
          style={{ cursor: c.isVac ? 'help' : 'default' }}
        >
          {c.isVac ? (
            // Square for vacation
            <rect x={c.x - 3.5} y={c.y - 3.5} width={7} height={7} rx={1}
              fill="var(--ink-3)" stroke="var(--ink-3)" strokeWidth="1"
              opacity={0.6} />
          ) : (
            // Circle for normal
            <circle cx={c.x} cy={c.y} r={3.5}
              fill={i === pts.length - 1 ? lineColor : 'var(--surface)'}
              stroke={lineColor} strokeWidth="1.5" />
          )}
          <text x={c.x} y={topPad + plotH + 12} textAnchor="middle"
            fontSize="10" fontWeight={i === pts.length - 1 && !c.isVac ? '600' : '400'}
            fill={c.isVac ? 'var(--ink-3)' : (i === pts.length - 1 ? 'var(--ink-1)' : 'var(--ink-3)')}
            fontStyle={c.isVac ? 'italic' : 'normal'}>
            {c.isVac ? absenceShort(absTypes[i] || 'vacation') : `${fmt(c.v)}${i === pts.length - 1 && unit ? unit : ''}`}
          </text>
        </g>
      ))}
      {/* Hover tooltip for absence */}
      {hovered !== null && coords[hovered].isVac && (
        <g>
          {(() => {
            const label = absenceFull(absTypes[hovered] || 'vacation');
            const text = `${label} (${fmt(coords[hovered].v)}${unit})`;
            const tw = text.length * 5.5 + 12;
            return (<>
              <rect x={coords[hovered].x - tw / 2} y={coords[hovered].y - 22} width={tw} height={18} rx={3}
                fill="var(--surface)" stroke="var(--line-strong)" strokeWidth="1" />
              <text x={coords[hovered].x} y={coords[hovered].y - 9} textAnchor="middle"
                fontSize="9" fill="var(--ink-2)">{text}</text>
            </>);
          })()}
        </g>
      )}
    </svg>
  );
}

const TIMELINE_ICONS: Record<string, { icon: string; color: string }> = {
  bsp: { icon: '📋', color: 'var(--accent)' },
  measure_start: { icon: '▶', color: 'var(--ok)' },
  measure_end: { icon: '■', color: 'var(--err)' },
  report: { icon: '📄', color: 'var(--warn)' },
  absence: { icon: '✕', color: 'var(--err)' },
  journal: { icon: '✎', color: 'var(--accent)' },
  default: { icon: '●', color: 'var(--ink-3)' },
};

// ── Objectifs panel helper components ────────────────────────────────────
function ObjectifSection({
  eyebrow, eyebrowBadge, eyebrowBadgeKind = 'neutral',
  actionLabel, actionTitle, onAction, children,
}: {
  eyebrow: string;
  eyebrowBadge?: string;
  eyebrowBadgeKind?: 'neutral' | 'accent';
  actionLabel?: string;
  actionTitle?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      border: '1px solid var(--line)',
      borderRadius: 'var(--r1)',
      background: 'var(--surface)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s8)',
        padding: 'var(--s6) var(--s10)',
        background: 'var(--bg)',
        borderBottom: '1px solid var(--line)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s6)' }}>
          <span style={{
            fontSize: 'var(--t-10)',
            fontWeight: 700,
            color: 'var(--ink-2)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>{eyebrow}</span>
          {eyebrowBadge && (
            <span style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '1px 6px',
              borderRadius: 10,
              background: eyebrowBadgeKind === 'accent'
                ? 'color-mix(in srgb, var(--accent) 14%, var(--surface))'
                : 'color-mix(in srgb, var(--ink-3) 14%, var(--surface))',
              color: eyebrowBadgeKind === 'accent' ? 'var(--accent)' : 'var(--ink-2)',
            }}>{eyebrowBadge}</span>
          )}
        </div>
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            title={actionTitle}
            style={{
              padding: '2px 8px',
              fontSize: 'var(--t-11)',
              fontWeight: 600,
              borderRadius: 'var(--r2)',
              border: '1px solid var(--accent)',
              background: 'var(--surface)',
              color: 'var(--accent)',
              cursor: 'pointer',
            }}
          >
            {actionLabel}
          </button>
        )}
      </div>
      <div style={{ padding: 'var(--s10)', display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
        {children}
      </div>
    </div>
  );
}

function MetaGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 'var(--s12)', rowGap: 4, fontSize: 'var(--t-12)' }}>
      {children}
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ color: 'var(--ink-1)' }}>{children}</span>
    </>
  );
}

function ObjectivesList({
  label, items, tone = 'normal',
}: {
  label: string;
  items: string[];
  tone?: 'normal' | 'muted';
}) {
  return (
    <div>
      <div style={{
        fontSize: 'var(--t-10)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--ink-3)',
        marginBottom: 'var(--s4)',
      }}>{label}</div>
      <ul style={{
        margin: 0,
        paddingLeft: 'var(--s16)',
        fontSize: tone === 'muted' ? 'var(--t-11)' : 'var(--t-12)',
        color: tone === 'muted' ? 'var(--ink-2)' : 'var(--ink-1)',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}

interface ParticipantDrilldownProps {
  /** Owned by a participant tab in App; when absent (direct mount), the
   * '#participant?id=..' hash is parsed instead. Props keep each mounted tab
   * pinned to its own participant regardless of the global hash. */
  participantId?: number;
  measureId?: number;
  /** Reports the person's family name once loaded, so a tab opened without a
   * label (deep link, equipe, alert) can name itself. */
  onNameLoaded?: (id: number, familyName: string) => void;
}

export function ParticipantDrilldown({ participantId: participantIdProp, measureId: measureIdProp, onNameLoaded }: ParticipantDrilldownProps) {
  const { t } = useT();
  const fd = useFormatDate();
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.split('?')[1] || '');
  const participantId = participantIdProp ?? Number(params.get('id'));
  const measureId = measureIdProp ?? (Number(params.get('measure')) || undefined);

  const [tab, setTab] = useState('overview');
  const [person, setPerson] = useState<any>(null);
  const [measures, setMeasures] = useState<any[]>([]);
  const [currentRef, setCurrentRef] = useState<any>(null);
  const [intake, setIntake] = useState<any>(null);
  const [bsp, setBsp] = useState<any>(null);
  const [goals, setGoals] = useState<any[]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<Array<{week: string, hours: number, days: number}>>([]);
  const [weeklyHistory, setWeeklyHistory] = useState<Array<{week: string, hours: number, present: number, total: number, presence_pct: number | null}>>([]);
  const [weeklyPlanned, setWeeklyPlanned] = useState<Array<{week: string, hours: number, is_future: boolean, planned?: number, attended?: number, missed_excused?: number, missed_unexcused?: number, unmarked?: number, unmarked_raw?: number}>>([]);
  const [lastWeekHours, setLastWeekHours] = useState<number | null>(null);
  const [avg4wHours, setAvg4wHours] = useState<number | null>(null);
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const [journalRatio, setJournalRatio] = useState<number | null>(null);
  const [journalCount3mo, setJournalCount3mo] = useState<number>(0);
  const [attendanceDays3mo, setAttendanceDays3mo] = useState<number>(0);
  const [excusedAbs3mo, setExcusedAbs3mo] = useState<number>(0);
  const [unexcusedAbs3mo, setUnexcusedAbs3mo] = useState<number>(0);
  const [graceRemaining, setGraceRemaining] = useState<number>(3);
  const [sickDaysUsed, setSickDaysUsed] = useState<number>(0);
  const [sickDaysAllowed, setSickDaysAllowed] = useState<number>(0);
  const [sickDaysRemaining, setSickDaysRemaining] = useState<number>(0);
  const [atelierMisses, setAtelierMisses] = useState<number>(0);
  const [atelierGraceRemaining, setAtelierGraceRemaining] = useState<number>(3);
  const [unexcusedAbsences, setUnexcusedAbsences] = useState<number>(0);
  const [monthsAtEveil, setMonthsAtEveil] = useState<number>(0);
  const [tauxActivite, setTauxActivite] = useState<number | null>(null);
  const [engagementRate, setEngagementRate] = useState<number | null>(null);
  const [hoursPerWeekServer, setHoursPerWeekServer] = useState<number | null>(null);
  const [programmedHPerWeek, setProgrammedHPerWeek] = useState<number | null>(null);
  const [presenceRate30d, setPresenceRate30d] = useState<number | null>(null);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [attStats, setAttStats] = useState<any>(null);
  const [monthlySummary, setMonthlySummary] = useState<any[]>([]);
  const [absences, setAbsences] = useState<any[]>([]);
  const [journal, setJournal] = useState<any[]>([]);
  const [journalDateFrom, setJournalDateFrom] = useState('');
  const [journalDateTo, setJournalDateTo] = useState('');
  const [reports, setReports] = useState<any[]>([]);
  const [ateliers, setAteliers] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [evaluations, setEvaluations] = useState<Array<{code: string, label: string, avg_score: number, eval_count: number}>>([]);
  const [goalEntries, setGoalEntries] = useState<Array<{entry_date: string, author: string | null, content: string | null}>>([]);
  const [placements, setPlacements] = useState<{total_days: number, days_3mo: number, total_hours: number} | null>(null);
  const [appointmentsUpcoming, setAppointmentsUpcoming] = useState<any[]>([]);
  const [appointmentsRecent, setAppointmentsRecent] = useState<any[]>([]);
  const [appointmentCounts, setAppointmentCounts] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [measuresData, setMeasuresData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Documents (OCR'd PDFs ingested into rm_ocr.*). Fetched once per participant.
  const [docsByMeasure, setDocsByMeasure] = useState<Record<string, DocumentSummary[]>>({});
  const [docsCurrent, setDocsCurrent] = useState<DocumentSummary[]>([]);
  const [docsUnattributed, setDocsUnattributed] = useState<DocumentSummary[]>([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentSummary | null>(null);
  // Raw "fichiers" (file_attachment) — incl. the OAI mandat IP-REA — served over SFTP
  const [files, setFiles] = useState<any[]>([]);

  // Adjustment modal — target mesure id; null = closed
  const [adjustingMeasureId, setAdjustingMeasureId] = useState<number | null>(null);
  // Bumps to re-fetch plans after a successful adjustment
  const [planVersion, setPlanVersion] = useState(0);
  // Bumps to re-fetch communications when an adjustment is saved
  const [commsRefresh, setCommsRefresh] = useState(0);
  // Unsent communications count (for tab badge)
  const [unsentComms, setUnsentComms] = useState(0);
  // Appointment detail modal (opens when clicking a RDV row with extensive notes)
  const [previewAppointment, setPreviewAppointment] = useState<AppointmentDetail | null>(null);

  // OAI art.14 timeline (only populated for OAI participants)
  const [oaiTimeline, setOaiTimeline] = useState<OaiTimelineData | null>(null);
  // RI parcours timeline (only populated for RI participants)
  const [riTimeline, setRiTimeline] = useState<RiTimelineData | null>(null);

  useEffect(() => {
    if (!participantId) return;
    setLoading(true);

    (api.getParticipant(participantId) as Promise<any>).then(data => {
      setPerson(data.person);
      if (onNameLoaded && data.person?.last_name) onNameLoaded(participantId, data.person.last_name);
      setMeasures(data.measures);
      setCurrentRef(data.currentReference);
      setIntake(data.intake);
      setBsp(data.bsp);
      setGoals(data.goals);
      setWeeklyTrend(data.weeklyTrend || []);
      setWeeklyHistory(data.weeklyHistory || []);
      setWeeklyPlanned(data.weeklyPlanned || []);
      setLastWeekHours(data.last_week_hours ?? null);
      setAvg4wHours(data.avg_4w_hours ?? null);
      setEvaluations(data.evaluations || []);
      setJournalRatio(data.journal_ratio);
      setJournalCount3mo(data.journal_count_3mo || 0);
      setAttendanceDays3mo(data.attendance_days_3mo || 0);
      setExcusedAbs3mo(data.excused_absences_3mo || 0);
      setUnexcusedAbs3mo(data.unexcused_absences_3mo || 0);
      setGraceRemaining(data.grace_remaining ?? 3);
      setSickDaysUsed(data.sick_days_used ?? 0);
      setSickDaysAllowed(data.sick_days_allowed ?? 0);
      setSickDaysRemaining(data.sick_days_remaining ?? 0);
      setAtelierMisses(data.atelier_misses ?? 0);
      setAtelierGraceRemaining(data.atelier_grace_remaining ?? 3);
      setUnexcusedAbsences(data.unexcused_absences ?? 0);
      setMonthsAtEveil(data.months_at_eveil ?? 0);
      setTauxActivite(data.taux_activite ?? null);
      setEngagementRate(data.engagement_rate ?? null);
      setHoursPerWeekServer(data.hours_per_week ?? null);
      setProgrammedHPerWeek(data.programmed_h_per_week ?? null);
      setPresenceRate30d(data.presence_rate_30d ?? null);
      setGoalEntries(data.goal_entries || []);
      setAppointmentsUpcoming(data.appointments_upcoming || []);
      setAppointmentsRecent(data.appointments_recent || []);
      setAppointmentCounts(data.appointment_counts || []);
      setOaiTimeline(data.oai_timeline ?? null);
      setRiTimeline(data.ri_timeline ?? null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [participantId]);

  // Attendance & Activities tab data
  useEffect(() => {
    if (!participantId || tab !== 'attendance') return;
    Promise.all([
      (api.getAttendance(participantId) as Promise<any>),
      (api.getActivities(participantId) as Promise<any>),
    ]).then(([attData, actData]) => {
      setAttendance(attData.attendance);
      setAttStats(attData.stats);
      setMonthlySummary(attData.monthlySummary);
      setAbsences(attData.absences || []);
      setAteliers(actData.ateliers || []);
      setEnrollments(actData.enrollments || []);
      setPlacements(actData.placements || null);
    }).catch(console.error);
  }, [participantId, tab]);

  // Journal & Reports tab data
  useEffect(() => {
    if (!participantId || tab !== 'journal') return;
    Promise.all([
      (api.getJournal(participantId) as Promise<any>),
      (api.getReports(participantId) as Promise<any>),
    ]).then(([jData, rData]) => {
      setJournal(jData.entries);
      setReports(rData.reports);
    }).catch(console.error);
  }, [participantId, measureId, tab]);

  // Measures tab data
  useEffect(() => {
    if (!participantId || tab !== 'measures') return;
    (api.getMeasures(participantId) as Promise<any>).then(data => {
      setMeasuresData(data.measures || []);
    }).catch(console.error);
  }, [participantId, tab]);

  // Timeline tab data
  useEffect(() => {
    if (!participantId || tab !== 'timeline') return;
    (api.getTimeline(participantId) as Promise<any>).then(data => {
      setTimeline(data.events || []);
    }).catch(console.error);
  }, [participantId, tab]);

  // Currently effective progression plan for the active mesure
  // (rm_ocr.measure_current_plan — RM adjustments override OAI decisions).
  const [progressionPlan, setProgressionPlan] = useState<{
    progression_type: 'progressive' | 'constant';
    start_pct: number | null;
    target_pct: number;
    period_start: string;
    period_end: string | null;
    target_date: string | null;
    qualitative_objectives: string[] | null;
    source: 'oai_decision' | 'rm_adjustment';
    source_id: number;
    source_created_at: string | null;
    reason: string | null;
    created_by: string | null;
    oai_source_document_id: number | null;
    oai_raw_phrase: string | null;
    oai_plan_id: number | null;
    rm_adjustment_id: number | null;
  } | null>(null);

  // Fallback when no progression plan was inserted (e.g., the OAI Decision PDF
  // didn't specify a target_pct). Holds the raw extraction so the Objectifs
  // panel still has something to show.
  const [extractedDecision, setExtractedDecision] = useState<{
    document_id: number;
    extracted_at: string | null;
    programme_label: string | null;
    progression_type: 'progressive' | 'constant' | null;
    start_pct: number | null;
    target_pct: number | null;
    target_month: string | null;
    target_date: string | null;
    raw_phrase: string | null;
    qualitative_objectives: string[];
  } | null>(null);

  // Latest BSP document (extracted Fiche premier entretien from PDFs) — distinct
  // from the existing `bsp` state above which holds eveil_v2.bsp data.
  const [bspDoc, setBspDoc] = useState<{
    document_id: number;
    source_file_id: number;
    filename: string;
    extracted_fields: Record<string, any>;
  } | null>(null);

  // All progression plans for this participant, keyed by measure_id (for Mesures tab).
  // Comes from rm_ocr.measure_current_plan: RM adjustments override OAI decisions.
  // `timeline` is the full ordered sequence of plan segments per mesure
  // (OAI plan + non-superseded adjustments) — used by the chart so historical
  // weeks stay anchored to the plan that was in effect at that time.
  const [plansByMeasure, setPlansByMeasure] = useState<Record<string, {
    measure_id: number;
    participant_id: number;
    progression_type: 'progressive' | 'constant';
    start_pct: number | null;
    target_pct: number;
    period_start: string;
    period_end: string | null;
    target_date: string | null;
    qualitative_objectives: string[] | null;
    source: 'oai_decision' | 'rm_adjustment';
    source_id: number;
    source_created_at: string | null;
    reason: string | null;
    created_by: string | null;
    oai_source_document_id: number | null;
    oai_raw_phrase: string | null;
    oai_plan_id: number | null;
    rm_adjustment_id: number | null;
    timeline: Array<{
      source: 'oai_decision' | 'rm_adjustment';
      source_id: number;
      progression_type: 'progressive' | 'constant';
      start_pct: number | null;
      target_pct: number;
      period_start: string;
      period_end: string | null;
      target_date: string | null;
      qualitative_objectives: string[] | null;
      reason: string | null;
      created_by: string | null;
      effective_from: string;
      effective_to: string | null;
      source_created_at: string | null;
      oai_source_document_id: number | null;
    }>;
  }>>({});

  // Documents (OCR'd PDFs) — fetched once per participant.
  useEffect(() => {
    if (!participantId) return;
    setDocsLoaded(false);
    (api.getParticipantFiles(participantId) as Promise<any>)
      .then((data) => setFiles(data.files || []))
      .catch(() => setFiles([]));
    (api.getParticipantDocuments(participantId) as Promise<any>)
      .then((data) => {
        const byMeasure: Record<string, DocumentSummary[]> = {};
        for (const [k, v] of Object.entries(data.byMeasure || {})) {
          byMeasure[k] = (v as { docs: DocumentSummary[] }).docs;
        }
        setDocsByMeasure(byMeasure);
        setDocsCurrent(data.current || []);
        setDocsUnattributed(data.unattributed || []);
      })
      .catch(console.error)
      .finally(() => setDocsLoaded(true));
  }, [participantId]);

  // Latest BSP for this participant (qualitative objectives, RI track especially).
  useEffect(() => {
    if (!participantId) return;
    (api.getParticipantBsp(participantId) as Promise<any>)
      .then((data) => setBspDoc(data?.bsp ?? null))
      .catch(() => setBspDoc(null));
  }, [participantId]);

  // Unsent communications count — refreshed whenever an adjustment is saved.
  useEffect(() => {
    if (!participantId) return;
    (api.getParticipantCommunications(participantId) as Promise<any>)
      .then((data) => setUnsentComms(data?.unsent ?? 0))
      .catch(() => setUnsentComms(0));
  }, [participantId, commsRefresh]);

  // All progression plans (for per-measure rendering in Mesures tab).
  // Re-fetches when planVersion bumps (after an adjustment is saved).
  useEffect(() => {
    if (!participantId) return;
    (api.getParticipantProgressionPlans(participantId) as Promise<any>)
      .then((data) => setPlansByMeasure(data?.byMeasure ?? {}))
      .catch(() => setPlansByMeasure({}));
  }, [participantId, planVersion]);

  // Progression plan for the active mesure (drives Objectifs panel + chart baseline).
  useEffect(() => {
    const activeId = measures.find((m: any) =>
      (m.status_code === 'active' || m.status_code === 'confirmed') &&
      (m.end_date === null || m.end_date >= new Date().toISOString().slice(0, 10))
    )?.id;
    if (!activeId) {
      setProgressionPlan(null);
      setExtractedDecision(null);
      return;
    }
    (api.getMeasureProgressionPlan(activeId) as Promise<any>)
      .then((data) => {
        setProgressionPlan(data?.plan ?? null);
        setExtractedDecision(data?.extractedDecision ?? null);
      })
      .catch(() => {
        setProgressionPlan(null);
        setExtractedDecision(null);
      });
  }, [measures, planVersion]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>;
  }

  if (!person) {
    return <p className="text-ink-3">{t('drilldown.notFound')}</p>;
  }

  const activeMeasure = measures.find((m: any) => m.status_code === 'active') || measures[0];

  return (
    <div>
      <div className="participant-header">
        <h1 className="participant-header__name">
          {person.first_name} {person.last_name}
        </h1>
      </div>

      <TabBar
        tabs={BASE_TABS.map(bt => ({
          key: bt.key,
          label: t(bt.labelKey),
          badge: bt.key === 'communications' ? unsentComms : null,
        }))}
        activeTab={tab}
        onTabChange={setTab}
      />

      {/* ── Tab 1: Overview ── */}
      {tab === 'overview' && (() => {
        // Compute trend data points from weeklyHistory (4-week rolling avg at 4 time offsets)
        const computeRollingAvg = (endIdx: number, window: number): number | null => {
          if (weeklyHistory.length === 0) return null;
          const clampEnd = Math.min(endIdx, weeklyHistory.length - 1);
          const startIdx = Math.max(0, clampEnd - window + 1);
          const slice = weeklyHistory.slice(startIdx, clampEnd + 1).filter(w => w.hours > 0);
          if (slice.length === 0) return null;
          return Math.round((slice.reduce((s, w) => s + w.hours, 0) / slice.length) * 10) / 10;
        };

        const len = weeklyHistory.length;
        const trendNow = avg4wHours;
        const trend6w = computeRollingAvg(len - 7, 4);

        // Determine overall trend direction
        let trendDir: 'up' | 'down' | 'stable' = 'stable';
        if (trendNow != null && trend6w != null && trend6w > 0) {
          const change = (trendNow - trend6w) / trend6w;
          if (change > 0.10) trendDir = 'up';
          else if (change < -0.10) trendDir = 'down';
        }

        // Delta vs 4-week average
        const delta4w = (lastWeekHours != null && avg4wHours != null) ? lastWeekHours - avg4wHours : null;
        const isDeclining = trendDir === 'down';

        // 4-week trend data for all three KPIs (absence weeks show TRUE hours — imputation removed)
        const last4 = weeklyHistory.slice(-4);
        const sparkData = last4.map((w: any) => w.hours);
        const sparkVacations = last4.map((w: any) => !!w.is_vacation);
        const sparkAbsenceTypes = last4.map((w: any) => w.absence_type || null);
        const tauxSparkData = last4.map((w: any) => Math.round((w.hours / 40) * 100));
        const presenceSparkData = last4.map((w: any) => w.presence_pct ?? 0);

        // Deltas for taux and presence — use non-vacation weeks only
        const last4Real = last4.filter((w: any) => !w.is_vacation);
        const tauxRealValues = last4Real.map((w: any) => Math.round((w.hours / 40) * 100));
        const presRealValues = last4Real.map((w: any) => w.presence_pct ?? 0);

        // Prévu vs Réalisé — last 4 completed weeks as % of a 40h week,
        // from the canonical decomposition (planned = accounted + unmarked).
        const planVsRealWeeks = (weeklyPlanned || [])
          .filter((w: any) => !w.is_future && !w.is_current && w.planned != null)
          .slice(-4)
          .map((w: any) => ({
            planned: Math.round((w.planned / 40) * 100),
            achieved: Math.round(((w.attended ?? 0) / 40) * 100),
            planned_h: w.planned ?? 0,
            achieved_h: w.attended ?? 0,
          }));
        const pvrLast = planVsRealWeeks.length > 0 ? planVsRealWeeks[planVsRealWeeks.length - 1] : null;
        const pvrAvg = planVsRealWeeks.length > 0 ? {
          planned: Math.round(planVsRealWeeks.reduce((s: number, w: any) => s + w.planned, 0) / planVsRealWeeks.length),
          achieved: Math.round(planVsRealWeeks.reduce((s: number, w: any) => s + w.achieved, 0) / planVsRealWeeks.length),
        } : null;
        const pvrAvgH = planVsRealWeeks.length > 0 ? {
          planned_h: planVsRealWeeks.reduce((s: number, w: any) => s + w.planned_h, 0) / planVsRealWeeks.length,
          achieved_h: planVsRealWeeks.reduce((s: number, w: any) => s + w.achieved_h, 0) / planVsRealWeeks.length,
        } : null;

        const tauxLast = tauxRealValues.length > 0 ? tauxRealValues[tauxRealValues.length - 1] : null;
        const tauxAvg = tauxRealValues.length > 0 ? Math.round(tauxRealValues.reduce((s: number, v: number) => s + v, 0) / tauxRealValues.length) : null;
        const tauxDelta = (tauxLast != null && tauxAvg != null && tauxRealValues.length >= 2) ? tauxLast - tauxAvg : null;

        // AI-prescribed "expected today" rate (linear progression from start_pct to target_pct).
        const tauxCible: number | null = (() => {
          if (!progressionPlan) return null;
          if (progressionPlan.progression_type === 'constant') return progressionPlan.target_pct;
          if (progressionPlan.progression_type !== 'progressive') return null;
          if (progressionPlan.start_pct == null || progressionPlan.target_date == null) return null;
          const t = Date.now();
          const t0 = new Date(progressionPlan.period_start).getTime();
          const t1 = new Date(progressionPlan.target_date).getTime();
          if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return null;
          const ratio = Math.min(1, Math.max(0, (t - t0) / (t1 - t0)));
          return Math.round(progressionPlan.start_pct + (progressionPlan.target_pct - progressionPlan.start_pct) * ratio);
        })();
        const hoursCible = tauxCible != null ? Math.round((tauxCible / 100) * 40 * 10) / 10 : null;

        const presLast = presRealValues.length > 0 ? presRealValues[presRealValues.length - 1] : null;
        const presAvg = presRealValues.length > 0 ? Math.round(presRealValues.reduce((s: number, v: number) => s + v, 0) / presRealValues.length) : null;
        const presDelta = (presLast != null && presAvg != null && presRealValues.length >= 2) ? presLast - presAvg : null;

        // ── Per-week mesure resolution + baseline (handles MULTIPLE mesures) ─────
        // For each week's Monday, find which mesure covers it (gives phase + label).
        // The chart fades prior-mesure weeks and uses each mesure's own plan for
        // the baseline target.
        //
        // All timestamps are computed in UTC so they stay consistent with
        // YYYY-MM-DD strings returned by the API (which JS parses as UTC
        // midnight). Mixing local and UTC dates caused boundary weeks to be
        // assigned to the wrong segment.
        const dateUtc = (iso: string): number => new Date(iso + 'T00:00:00Z').getTime();
        const isoWeekToMonday = (iso: string): number => {
          const [y, w] = iso.split('-').map(Number);
          const jan4 = new Date(Date.UTC(y, 0, 4));
          const dow = jan4.getUTCDay() || 7;
          const mon = new Date(jan4);
          mon.setUTCDate(jan4.getUTCDate() - dow + 1 + (w - 1) * 7);
          return mon.getTime();
        };
        const activeMeasureId = activeMeasure?.id ?? null;
        // Index mesures by start (asc) — pick the latest start that's ≤ week.
        const mesuresSortedAsc = [...measures].sort((a: any, b: any) =>
          dateUtc(a.start_date) - dateUtc(b.start_date)
        );
        function findMeasureForWeek(t: number): any | null {
          let pick: any | null = null;
          for (const m of mesuresSortedAsc) {
            const ms = dateUtc(m.start_date);
            if (ms > t) break;
            const me = m.end_date ? dateUtc(m.end_date) : Infinity;
            if (t <= me) pick = m;
          }
          return pick;
        }
        function mesureLabel(m: any): string {
          return m?.subtype_name || m?.type_label || '—';
        }

        // Chart data with measure_phase + measure_label per week.
        type WeeklyPoint = {
          week: string; value: number; is_vacation: boolean; absence_type: string | null;
          measure_phase: 'current' | 'prior'; measure_label?: string; is_future?: boolean;
        };
        // Weeks already covered by weeklyHistory; planned-only weeks (no attendance
        // recorded, or future weeks ahead of today) are filled in from weeklyPlanned
        // so the chart has no gaps and future planned bars are visible.
        const historyWeeks = new Set(weeklyHistory.map((w: any) => w.week));
        const extraWeeks = (weeklyPlanned || []).filter((p) => !historyWeeks.has(p.week));
        const buildPoints = (toValue: (w: any) => number, filterFn?: (w: any) => boolean, includeExtras = false): WeeklyPoint[] => {
          const src = filterFn ? weeklyHistory.filter(filterFn as any) : weeklyHistory;
          const past: WeeklyPoint[] = src.map((w: any) => {
            const t = isoWeekToMonday(w.week);
            const m = findMeasureForWeek(t);
            return {
              week: w.week,
              value: toValue(w),
              is_vacation: !!w.is_vacation,
              absence_type: w.absence_type ?? null,
              measure_phase: m && activeMeasureId && m.id === activeMeasureId ? 'current' : 'prior',
              measure_label: m ? mesureLabel(m) : undefined,
            };
          });
          if (!includeExtras) return past;
          const extras: WeeklyPoint[] = extraWeeks.map((p) => {
            const t = isoWeekToMonday(p.week);
            const m = findMeasureForWeek(t) || activeMeasure;
            return {
              week: p.week,
              value: 0,
              is_vacation: false,
              absence_type: null,
              measure_phase: m && activeMeasureId && m.id === activeMeasureId ? 'current' : 'prior',
              measure_label: m ? mesureLabel(m) : undefined,
              is_future: p.is_future,
            };
          });
          // Merge and sort chronologically by ISO week.
          return [...past, ...extras].sort((a, b) => a.week.localeCompare(b.week));
        };
        const hoursChartData = buildPoints((w) => w.hours, undefined, true);
        const tauxChartData = buildPoints((w) => Math.round((w.hours / 40) * 100), undefined, true);
        const presenceChartData = buildPoints((w) => w.presence_pct!, (w: any) => w.presence_pct != null);

        // Build the planned arrays aligned with each chart's data.
        const plannedByWeek = new Map<string, number>(
          (weeklyPlanned || []).map((p) => [p.week, p.hours])
        );
        // A14b decomposition, aligned with the hours chart (past weeks only)
        const decompByWeek = new Map<string, { attended: number; missed_excused: number; missed_unexcused: number; unmarked: number }>(
          (weeklyPlanned || [])
            .filter((p) => !p.is_future && p.planned != null)
            .map((p) => [p.week, {
              attended: p.attended ?? 0,
              missed_excused: p.missed_excused ?? 0,
              missed_unexcused: p.missed_unexcused ?? 0,
              unmarked: p.unmarked ?? 0,
            }])
        );
        const hoursDecomp = hoursChartData.map((p) => decompByWeek.get(p.week) ?? null);
        const hoursPlanned: (number | null)[] = hoursChartData.map((p) => {
          const v = plannedByWeek.get(p.week);
          return v != null ? Math.round(v * 10) / 10 : null;
        });
        const tauxPlanned: (number | null)[] = tauxChartData.map((p) => {
          const v = plannedByWeek.get(p.week);
          return v != null ? Math.round((v / 40) * 100) : null;
        });

        // Multi-mesure, multi-segment baseline.
        //
        // For each week's Monday t:
        //   1. Find the mesure that contains t (gives phase + label).
        //   2. From that mesure's timeline, find the segment whose
        //      [effective_from, effective_to) contains t.
        //   3. Use that segment's (start_pct, target_pct, period_start,
        //      target_date) for the interpolation — so weeks before an
        //      adjustment continue to use the previous (OAI) plan's curve,
        //      weeks after use the adjustment's curve.
        const computeMultiBaseline = (
          points: WeeklyPoint[],
          toUnit: (pct: number) => number,
        ): (number | null)[] | undefined => {
          if (points.length === 0) return undefined;
          let anyTarget = false;
          const series = points.map((p) => {
            const t = isoWeekToMonday(p.week);
            const m = findMeasureForWeek(t);
            if (!m) return null;
            const plan = plansByMeasure[String(m.id)];
            if (!plan) return null;
            const segments = plan.timeline || [];
            if (segments.length === 0) return null;

            // Find the active segment for this week.
            let seg: typeof segments[number] | null = null;
            for (const s of segments) {
              const from = dateUtc(s.effective_from);
              const to = s.effective_to ? dateUtc(s.effective_to) : Infinity;
              if (t >= from && t < to) { seg = s; break; }
            }
            // If no segment covers t (e.g. week before any plan), skip.
            if (!seg) return null;
            // Respect the OAI plan's period_end as the outer mesure boundary.
            if (plan.period_end && t > dateUtc(plan.period_end)) return null;

            const target = seg.target_pct;
            if (target == null) return null;
            const startPct = seg.start_pct;
            const ps = dateUtc(seg.period_start);
            const td = seg.target_date ? dateUtc(seg.target_date) : null;

            anyTarget = true;
            const isProgressive = seg.progression_type === 'progressive' && startPct != null && td;
            if (!isProgressive) return toUnit(target);
            // Clamp the interpolation ratio inside the segment's own window.
            const ratio = Math.min(1, Math.max(0, (t - ps) / (td! - ps)));
            const pct = startPct + (target - startPct) * ratio;
            return toUnit(pct);
          });
          return anyTarget ? series : undefined;
        };

        // For hours/week chart: pct → equivalent hours (40h = 100%).
        const hoursBaseline = computeMultiBaseline(hoursChartData, (pct) => Math.round((pct / 100) * 40 * 10) / 10);
        // For taux d'activité chart: pct → pct directly.
        const tauxBaseline = computeMultiBaseline(tauxChartData, (pct) => Math.round(pct));
        const baselineLabel = progressionPlan
          ? (progressionPlan.progression_type === 'progressive'
              ? t('drilldown.chart.aiTargetProgressive', { start: progressionPlan.start_pct, target: progressionPlan.target_pct })
              : t('drilldown.chart.aiTargetConstant', { target: progressionPlan.target_pct }))
          : undefined;

        const toggleMetric = (key: string) => setExpandedMetric(prev => prev === key ? null : key);

        const hasBspOrIntake = bsp || intake;

        // Compute age from DOB
        const computeAge = (dob: string | null | undefined): string => {
          if (!dob) return '';
          const birth = new Date(dob);
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          return ` (${t('drilldown.personalInfo.ageYears', { n: age })})`;
        };

        // Initials helper
        const initials = (first?: string | null, last?: string | null): string => {
          return [(first || '')[0], (last || '')[0]].filter(Boolean).join('').toUpperCase() || '?';
        };

        // Measure progress computation
        const measureStart = activeMeasure?.start_date ? new Date(activeMeasure.start_date) : null;
        const measureEnd = activeMeasure?.end_date ? new Date(activeMeasure.end_date) : null;
        const now = new Date();
        const totalDays = measureStart && measureEnd ? Math.max(1, Math.ceil((measureEnd.getTime() - measureStart.getTime()) / (1000 * 60 * 60 * 24))) : null;
        const elapsedDays = measureStart ? Math.max(0, Math.ceil((now.getTime() - measureStart.getTime()) / (1000 * 60 * 60 * 24))) : null;
        const measurePct = totalDays && elapsedDays != null ? Math.min(100, Math.round((elapsedDays / totalDays) * 100)) : 0;

        return (
        <div className="overview-layout">

          {/* ═══ Mesure active — spanning banner ═══ */}
          {activeMeasure ? (
            <div className="measure-banner">
              <div className="measure-banner__info" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', flexWrap: 'wrap' }}>
                  <span className="measure-banner__type">{activeMeasure.subtype_name || activeMeasure.type_label}</span>
                  {activeMeasure.mandant_name && (
                    <span className="measure-banner__mandant">{activeMeasure.mandant_name}</span>
                  )}
                  {activeMeasure.contract_number && (
                    <span className="measure-banner__contract">{t('drilldown.measureBanner.contract', { n: activeMeasure.contract_number })}</span>
                  )}
                </div>
                <span className="badge badge-ok badge-sm" style={{ flexShrink: 0 }}>{activeMeasure.status_label}</span>
              </div>
              {measureStart && measureEnd && (
                <div className="measure-banner__progress">
                  <div className="measure-progress">
                    <div className="measure-progress__bar">
                      <div className="measure-progress__fill" style={{ width: `${measurePct}%` }} />
                    </div>
                    <div className="measure-progress__labels">
                      <span>{formatDate(activeMeasure.start_date)}</span>
                      {elapsedDays != null && totalDays != null && (
                        <span>{t('drilldown.measureBanner.daysProgress', { elapsed: elapsedDays, total: totalDays })}</span>
                      )}
                      <span>{formatDate(activeMeasure.end_date)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="measure-banner">
              <span className="empty-text">{t('drilldown.measureBanner.none')}</span>
            </div>
          )}

          {/* ═══ KPI Row — 4 cards including Absences ═══ */}
          <div className="kpi-row">
            {/* Activité effective */}
            <div
              className={`kpi-card${expandedMetric === 'activite' ? ' kpi-card--expanded' : ''}`}
              onClick={() => toggleMetric('activite')}
              style={{ cursor: 'pointer' }}
            >
              <div className="kpi-card__label">
                {t('drilldown.kpi.activity.label')}
                <InfoTip text={t('drilldown.kpi.activity.info')} />
              </div>
              <div className="kpi-card__body">
                <div>
                  <span className="kpi-card__value">
                    {avg4wHours != null ? avg4wHours.toFixed(1).replace('.', ',') : (lastWeekHours != null ? lastWeekHours.toFixed(1).replace('.', ',') : '—')}
                  </span>
                  <span className="kpi-card__suffix">{t('drilldown.kpi.hoursPerWeekSuffix')}</span>
                  {lastWeekHours != null && avg4wHours != null && lastWeekHours !== avg4wHours && (
                    <div className="kpi-card__sub" style={{ color: 'var(--ink-3)' }}>
                      {t('drilldown.kpi.lastWeekSub', { hours: lastWeekHours.toFixed(1).replace('.', ',') })}
                    </div>
                  )}
                  {hoursCible != null && (
                    <div
                      className="kpi-card__sub"
                      title={progressionPlan?.progression_type === 'progressive'
                        ? t('drilldown.kpi.aiTargetTitleProgressive', { start: progressionPlan.start_pct, target: progressionPlan.target_pct })
                        : t('drilldown.kpi.aiTargetTitleConstant', { target: progressionPlan?.target_pct })}
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {t('drilldown.kpi.aiTargetHours', { hours: hoursCible.toFixed(1).replace('.', ','), pct: tauxCible })}
                    </div>
                  )}
                </div>
              </div>
              {tauxAvg != null && (
                <div className="kpi-card__sub">{t('drilldown.kpi.rate.inline', { pct: tauxAvg })}</div>
              )}
              {engagementRate != null && tauxAvg != null && engagementRate !== tauxAvg && (
                <div className="kpi-card__sub" title={t('drilldown.kpi.engagement.info')} style={{ color: 'var(--ink-3)' }}>
                  {t('drilldown.kpi.engagement.value', { pct: engagementRate })}
                </div>
              )}
              {delta4w != null && (
                <div className={`kpi-card__delta kpi-card__delta--${delta4w >= 0 ? 'up' : 'down'}`}>
                  {delta4w >= 0 ? '▲' : '▼'} {t('drilldown.kpi.deltaHours', { n: Math.abs(delta4w).toFixed(1).replace('.', ',') })}
                </div>
              )}
              <FourWeekTrend values={sparkData} unit="h" vacations={sparkVacations} absenceTypes={sparkAbsenceTypes} formatVal={v => v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', ',')} />
            </div>

            {/* Prévu vs Réalisé (4 semaines) */}
            <div
              className={`kpi-card${expandedMetric === 'taux' ? ' kpi-card--expanded' : ''}`}
              onClick={() => toggleMetric('taux')}
              style={{ cursor: 'pointer' }}
            >
              <div className="kpi-card__label">
                {t('drilldown.kpi.planVsReal.label')}
                <InfoTip text={t('drilldown.kpi.planVsReal.info')} />
              </div>
              <div className="kpi-card__body">
                <div>
                  <div style={{ fontSize: 'var(--t-12)', whiteSpace: 'nowrap' }}>
                    <span style={{ color: 'var(--warn)', fontWeight: 700 }}>
                      {t('drilldown.kpi.planVsReal.plannedWord')} {pvrLast != null ? `${pvrLast.planned} %` : '—'}
                    </span>
                    <span style={{ color: 'var(--ink-3)' }}> · </span>
                    <span style={{ color: pvrLast != null && pvrLast.achieved >= pvrLast.planned ? 'var(--ok)' : 'var(--accent)', fontWeight: 700 }}>
                      {t('drilldown.kpi.planVsReal.achievedWord')} {pvrLast != null ? `${pvrLast.achieved} %` : '—'}
                    </span>
                  </div>
                  <div className="kpi-card__sub">
                    {pvrLast != null
                      ? t('drilldown.kpi.planVsReal.hoursSub', {
                          planned_h: pvrLast.planned_h.toFixed(1).replace('.', ','),
                          achieved_h: pvrLast.achieved_h.toFixed(1).replace('.', ',') })
                      : t('drilldown.kpi.planVsReal.noPlan')}
                  </div>
                  {pvrAvg != null && (
                    <div className="kpi-card__sub" style={{ color: 'var(--ink-3)' }}>
                      {t('drilldown.kpi.planVsReal.avgSub', { planned: pvrAvg.planned, achieved: pvrAvg.achieved })}
                    </div>
                  )}
                </div>
              </div>
              {pvrLast != null && (
                <div className={`kpi-card__delta kpi-card__delta--${pvrLast.achieved >= pvrLast.planned ? 'up' : 'down'}`}>
                  {pvrLast.achieved >= pvrLast.planned ? '▲' : '▼'} {t('drilldown.kpi.planVsReal.deltaVs', { n: Math.abs(pvrLast.achieved - pvrLast.planned) })}
                </div>
              )}
              <PlannedVsAchievedTrend weeks={planVsRealWeeks} />
            </div>

            {/* Présence */}
            <div
              className={`kpi-card${expandedMetric === 'presence' ? ' kpi-card--expanded' : ''}`}
              onClick={() => toggleMetric('presence')}
              style={{ cursor: 'pointer' }}
            >
              <div className="kpi-card__label">
                {t('drilldown.kpi.presence.label')}
                <InfoTip text={t('drilldown.kpi.presence.info')} />
              </div>
              <div className="kpi-card__body">
                <div>
                  <span className="kpi-card__value kpi-card__value--lg">
                    {presLast != null ? Math.round(presLast) : '—'}
                  </span>
                  <span className="kpi-card__suffix">%</span>
                  <div className="kpi-card__sub">{t('drilldown.kpi.presence.sub')}</div>
                </div>
                <ProgressRing value={presLast ?? 0} max={100} size={48} />
              </div>
              {presDelta != null && (
                <div className={`kpi-card__delta kpi-card__delta--${presDelta >= 0 ? 'up' : 'down'}`}>
                  {presDelta >= 0 ? '▲' : '▼'} {t('drilldown.kpi.deltaPct', { n: Math.abs(presDelta) })}
                </div>
              )}
              <FourWeekTrend values={presenceSparkData} unit="%" vacations={sparkVacations} absenceTypes={sparkAbsenceTypes} formatVal={v => String(Math.round(v))} />
            </div>

            {/* Absences */}
            <div className="kpi-card">
              <div className="kpi-card__label">
                {t('drilldown.kpi.absences.label')}
                <InfoTip text={t('drilldown.kpi.absences.info', { months: monthsAtEveil, allowed: sickDaysAllowed })} />
              </div>
              <div className="absence-kpi">
                <div className="absence-kpi__row">
                  <span className="absence-kpi__label">{t('drilldown.kpi.absences.sick')}</span>
                  <span className={`absence-kpi__val${sickDaysRemaining <= 0 ? ' absence-kpi__val--alert' : ''}`}>{t('drilldown.unit.daysAbbrev', { n: sickDaysUsed })}</span>
                  <span className="absence-kpi__detail">{t('drilldown.kpi.absences.remaining', { remaining: sickDaysRemaining, total: sickDaysAllowed })}</span>
                </div>
                <div className="absence-kpi__row">
                  <span className="absence-kpi__label">{t('drilldown.kpi.absences.workshops')}</span>
                  <span className={`absence-kpi__val${atelierGraceRemaining <= 0 ? ' absence-kpi__val--alert' : ''}`}>{atelierMisses}</span>
                  <span className="absence-kpi__detail">{t('drilldown.kpi.absences.remaining', { remaining: atelierGraceRemaining, total: 3 })}</span>
                </div>
                <div className="absence-kpi__row">
                  <span className="absence-kpi__label">{t('drilldown.kpi.absences.unexcused')}</span>
                  <span className={`absence-kpi__val${unexcusedAbsences > 0 ? ' absence-kpi__val--alert' : ''}`}>{unexcusedAbsences}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ Expanded chart panel ═══ */}
          {expandedMetric && (
            <div className="metric-detail-panel">
              <div className="metric-detail-panel__title">
                {expandedMetric === 'activite' && t('drilldown.chart.title.activity')}
                {expandedMetric === 'taux' && t('drilldown.chart.title.rate')}
                {expandedMetric === 'presence' && t('drilldown.chart.title.presence')}
              </div>
              {expandedMetric === 'activite' && (
                <WeeklyChart data={hoursChartData} rollingWindow={4} unit="h" refLine={40} barColor="var(--warn)" height={180} baseline={hoursBaseline} baselineLabel={baselineLabel} planned={hoursPlanned} plannedLabel={t('drilldown.chart.plannedHours')} decomposition={hoursDecomp} />
              )}
              {expandedMetric === 'taux' && (
                <WeeklyChart data={tauxChartData} rollingWindow={4} unit="%" formatValue={v => `${Math.round(v)}`} height={180} baseline={tauxBaseline} baselineLabel={baselineLabel} planned={tauxPlanned} plannedLabel={t('drilldown.chart.plannedPct')} />
              )}
              {expandedMetric === 'presence' && (
                <WeeklyChart data={presenceChartData} rollingWindow={4} unit="%" formatValue={v => `${Math.round(v)}`} height={180} />
              )}
            </div>
          )}

          {/* ═══ Ligne de temps art. 14 (OAI uniquement) ═══ */}
          {oaiTimeline && activeMeasure && (
            <div style={{
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              backgroundColor: 'var(--surface)',
              padding: 'var(--s10) var(--s12)',
            }}>
              <MeasureTimeline
                measureStartDate={activeMeasure.start_date}
                measureEndDate={activeMeasure.end_date}
                timeline={oaiTimeline}
                currentTaux={tauxActivite}
              />
            </div>
          )}

          {/* ═══ Ligne de temps RI (CCC conv. uniquement) ═══ */}
          {riTimeline && (
            <div style={{
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              backgroundColor: 'var(--surface)',
              padding: 'var(--s10) var(--s12)',
            }}>
              <RiTimeline
                timeline={riTimeline}
                currentTaux={tauxActivite}
              />
            </div>
          )}

          {/* ═══ Upcoming Appointments ═══ */}
          {appointmentsUpcoming.length > 0 && (
            <section style={{ border: '1px solid var(--line)', borderRadius: 'var(--r1)', backgroundColor: 'var(--surface)', padding: 'var(--s8) var(--s12)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s8)', marginBottom: 'var(--s4)' }}>
                <span style={{ fontSize: 'var(--t-12)', fontWeight: 600, color: 'var(--ink-1)' }}>{t('drilldown.appointments.upcoming')}</span>
                {appointmentCounts.length > 0 && (
                  <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                    {t('drilldown.appointments.totalCount', { list: appointmentCounts.map((c: any) => `${c.count} ${c.type_name}`).join(', ') })}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s8)' }}>
                {appointmentsUpcoming.map((a: any) => (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--s8)',
                    padding: 'var(--s4) var(--s8)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r1)',
                    backgroundColor: 'color-mix(in srgb, var(--ok) 5%, var(--surface))',
                    fontSize: 'var(--t-12)',
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--ok)' }}>{formatDate(a.appointment_date)}</span>
                    <span className="badge badge-sm">{a.type_name}</span>
                    {a.start_time && (
                      <span style={{ color: 'var(--ink-3)' }}>{a.start_time.slice(0, 5)}{a.end_time ? `–${a.end_time.slice(0, 5)}` : ''}</span>
                    )}
                    {a.staff_name && (
                      <span style={{ color: 'var(--ink-2)' }}>{a.staff_name}</span>
                    )}
                    {a.comment && (
                      <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.comment}>{a.comment}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ═══ Body: details left, BSP right ═══ */}
          <div className={`overview-body${!hasBspOrIntake ? ' overview-body--single' : ''}`}>
            <div className="overview-body__left">
              {/* Contacts clés */}
              <div className="sidebar-card">
                <div className="sidebar-card__header">{t('drilldown.contacts.title')}</div>
                {currentRef && (
                  <>
                    <div className="contact-row">
                      <div className="contact-avatar">{initials(currentRef.rm_first_name, currentRef.rm_last_name)}</div>
                      <div className="contact-row__info">
                        <span className="contact-row__name">{currentRef.rm_first_name} {currentRef.rm_last_name}</span>
                        <span className="contact-row__role">RM</span>
                      </div>
                    </div>
                    {currentRef.rp_first_name && (
                      <div className="contact-row">
                        <div className="contact-avatar contact-avatar--ok">{initials(currentRef.rp_first_name, currentRef.rp_last_name)}</div>
                        <div className="contact-row__info">
                          <span className="contact-row__name">{currentRef.rp_first_name} {currentRef.rp_last_name}</span>
                          <span className="contact-row__role">RP</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {activeMeasure?.contact_first_name && (
                  <div className="contact-row">
                    <div className="contact-avatar contact-avatar--neutral">{initials(activeMeasure.contact_first_name, activeMeasure.contact_last_name)}</div>
                    <div className="contact-row__info">
                      <span className="contact-row__name">{activeMeasure.contact_first_name} {activeMeasure.contact_last_name}</span>
                      <span className="contact-row__role">
                        {t('drilldown.contacts.advisor')}{activeMeasure.mandant_name ? ` · ${activeMeasure.mandant_name}` : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Objectifs (extraits des mandats AI / fiches d'entretien) + Évaluations */}
              <div className="sidebar-card">
                <div className="sidebar-card__header">{t('drilldown.objectives.title')}</div>
                {(() => {
                  const oaiObjectives = progressionPlan?.qualitative_objectives ?? [];
                  // bspDoc returns either a BSP doc or a qualitative_report doc — both shapes
                  // share a common pattern but field names differ. Normalise here.
                  const ef: any = bspDoc?.extracted_fields ?? {};
                  const docTemplate = (bspDoc as any)?.extraction_template as string | undefined;
                  const isBsp = docTemplate === 'first_interview_bsp';
                  const docKind = ef.doc_kind as string | undefined;
                  const docLabel = isBsp
                    ? t('drilldown.objectives.bspSheet')
                    : docKind
                      ? t('drilldown.objectives.reportWithKind', { kind: docKind.replace(/_/g, ' ') })
                      : t('drilldown.objectives.report');
                  const stateLabel = isBsp ? 'mots du participant' : 'extrait du rapport';

                  // Combine objectives from both BSP-style and rapport-style shapes.
                  const partObjectives = [
                    ...((ef.objectifs_participant ?? []) as string[]),
                    ...((ef.participant_objectives ?? []) as string[]),
                  ];
                  const meansOrActions = [
                    ...((ef.moyens_atteindre ?? []) as string[]),
                    ...((ef.recommended_actions ?? []) as string[]),
                  ];
                  const motivations = (ef.participant_motivations ?? []) as string[];

                  const decisionHasContent = !!extractedDecision && (
                    !!extractedDecision.raw_phrase
                    || extractedDecision.start_pct != null
                    || !!extractedDecision.programme_label
                    || extractedDecision.qualitative_objectives.length > 0
                  );
                  const hasAny = oaiObjectives.length > 0 || partObjectives.length > 0 || progressionPlan != null || decisionHasContent;

                  if (!hasAny) {
                    return <p className="empty-text">{t('drilldown.objectives.empty')}</p>;
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
                      {/* ─── Taux d'activité card ─── */}
                      {progressionPlan && (
                        <ObjectifSection
                          eyebrow={t('drilldown.objectives.activityRate')}
                          eyebrowBadge={progressionPlan.source === 'rm_adjustment' ? t('drilldown.objectives.adjusted') : 'AI'}
                          eyebrowBadgeKind={progressionPlan.source === 'rm_adjustment' ? 'accent' : 'neutral'}
                          actionLabel={t('drilldown.objectives.adjustAction')}
                          actionTitle={t('drilldown.objectives.adjustActionTitle')}
                          onAction={() => activeMeasure && setAdjustingMeasureId(activeMeasure.id)}
                        >
                          <MetaGrid>
                            <MetaRow label={t('drilldown.objectives.progression')}>
                              <strong>
                                {progressionPlan.progression_type === 'progressive'
                                  ? t('drilldown.objectives.progressionRange', { start: progressionPlan.start_pct, target: progressionPlan.target_pct })
                                  : t('drilldown.objectives.progressionConstant', { target: progressionPlan.target_pct })}
                              </strong>
                            </MetaRow>
                            <MetaRow label={t('drilldown.objectives.period')}>
                              {formatDate(progressionPlan.period_start)} – {formatDate(progressionPlan.target_date || progressionPlan.period_end)}
                            </MetaRow>
                            <MetaRow label={t('drilldown.objectives.source')}>
                              {progressionPlan.source === 'rm_adjustment'
                                ? <>
                                    {t('drilldown.objectives.rmAdjustment')}
                                    {progressionPlan.source_created_at && (
                                      <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                                        · {formatDate(progressionPlan.source_created_at.slice(0, 10))}
                                      </span>
                                    )}
                                    {progressionPlan.created_by && (
                                      <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                                        · {progressionPlan.created_by}
                                      </span>
                                    )}
                                  </>
                                : <>
                                    {t('drilldown.objectives.aiMandate')}
                                    {progressionPlan.source_created_at && (
                                      <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                                        · {formatDate(progressionPlan.source_created_at.slice(0, 10))}
                                      </span>
                                    )}
                                    {progressionPlan.oai_source_document_id && (
                                      <a
                                        href={api.documentBlobUrl(progressionPlan.oai_source_document_id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ marginLeft: 'var(--s8)', fontSize: 'var(--t-11)', color: 'var(--accent)' }}
                                      >
                                        📄 PDF
                                      </a>
                                    )}
                                  </>
                              }
                            </MetaRow>
                          </MetaGrid>

                          {oaiObjectives.length > 0 && (
                            <ObjectivesList
                              label={t('drilldown.objectives.qualitativeObjectives')}
                              items={oaiObjectives}
                            />
                          )}

                          {/* History expander — shows OAI mandat + all RM adjustments,
                              with a delete (🗑) action per adjustment. */}
                          {activeMeasure && (() => {
                            const planForActive = plansByMeasure[String(activeMeasure.id)];
                            const oaiSegment = planForActive?.timeline?.find(s => s.source === 'oai_decision') ?? null;
                            return (
                              <AdjustmentHistory
                                measureId={activeMeasure.id}
                                version={planVersion}
                                oai={oaiSegment ? {
                                  source_document_id: oaiSegment.oai_source_document_id ?? null,
                                  progression_type: oaiSegment.progression_type,
                                  start_pct: oaiSegment.start_pct,
                                  target_pct: oaiSegment.target_pct,
                                  period_start: oaiSegment.period_start,
                                  target_date: oaiSegment.target_date,
                                  source_created_at: oaiSegment.source_created_at,
                                } : null}
                                onChange={() => {
                                  setPlanVersion(v => v + 1);
                                  setCommsRefresh(v => v + 1);
                                }}
                              />
                            );
                          })()}
                        </ObjectifSection>
                      )}

                      {/* ─── OAI Decision analysée (fallback) ─────────────────────────
                          Shown when no actionable progression plan exists for the active
                          mesure but the OAI Decision PDF *was* OCR'd and analysed. We
                          surface whatever was extracted so the RM sees the document's
                          content (programme, raw phrase, start_pct, qualitative goals)
                          even if no quantifiable target was prescribed.
                          ──────────────────────────────────────────────────────────── */}
                      {!progressionPlan && extractedDecision && decisionHasContent && (
                        <ObjectifSection
                          eyebrow={t('drilldown.objectives.aiMandate')}
                          eyebrowBadge={t('drilldown.objectives.noQuantTarget')}
                          eyebrowBadgeKind="neutral"
                        >
                          <MetaGrid>
                            {extractedDecision.programme_label && (
                              <MetaRow label={t('drilldown.objectives.programme')}>
                                <strong>{extractedDecision.programme_label}</strong>
                              </MetaRow>
                            )}
                            {extractedDecision.progression_type && (
                              <MetaRow label={t('drilldown.objectives.type')}>
                                {extractedDecision.progression_type === 'progressive' ? t('drilldown.objectives.progression') : t('drilldown.objectives.constant')}
                                {extractedDecision.start_pct != null && extractedDecision.progression_type === 'progressive' && (
                                  <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                                    · {t('drilldown.objectives.startPct', { pct: extractedDecision.start_pct })}
                                  </span>
                                )}
                              </MetaRow>
                            )}
                            {extractedDecision.raw_phrase && (
                              <MetaRow label={t('drilldown.objectives.mention')}>
                                <em style={{ color: 'var(--ink-2)' }}>« {extractedDecision.raw_phrase} »</em>
                              </MetaRow>
                            )}
                            <MetaRow label={t('drilldown.objectives.source')}>
                              <>
                                {t('drilldown.objectives.aiMandate')}
                                {extractedDecision.extracted_at && (
                                  <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>
                                    · {formatDate(extractedDecision.extracted_at.slice(0, 10))}
                                  </span>
                                )}
                                <a
                                  href={api.documentBlobUrl(extractedDecision.document_id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ marginLeft: 'var(--s8)', fontSize: 'var(--t-11)', color: 'var(--accent)' }}
                                >
                                  📄 PDF
                                </a>
                              </>
                            </MetaRow>
                          </MetaGrid>

                          {extractedDecision.qualitative_objectives.length > 0 && (
                            <ObjectivesList
                              label={t('drilldown.objectives.qualitativeObjectives')}
                              items={extractedDecision.qualitative_objectives}
                            />
                          )}
                        </ObjectifSection>
                      )}

                      {/* ─── Document participant card ─── */}
                      {(partObjectives.length > 0 || meansOrActions.length > 0 || motivations.length > 0) && (
                        <ObjectifSection
                          eyebrow={isBsp ? t('drilldown.objectives.bspSheet') : t('drilldown.objectives.participantReport')}
                          eyebrowBadge={isBsp ? t('drilldown.objectives.participantWords') : t('drilldown.objectives.reportExcerpt')}
                          eyebrowBadgeKind="neutral"
                        >
                          {bspDoc && (
                            <MetaGrid>
                              <MetaRow label={t('drilldown.objectives.type')}>
                                {docLabel}
                              </MetaRow>
                              <MetaRow label={t('drilldown.objectives.source')}>
                                <a
                                  href={api.documentBlobUrl(bspDoc.document_id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: 'var(--accent)' }}
                                >
                                  📄 {t('drilldown.objectives.viewPdf')}
                                </a>
                              </MetaRow>
                            </MetaGrid>
                          )}

                          {partObjectives.length > 0 && (
                            <ObjectivesList
                              label={t('drilldown.objectives.personalObjectives')}
                              items={partObjectives}
                            />
                          )}
                          {meansOrActions.length > 0 && (
                            <ObjectivesList
                              label={isBsp ? t('drilldown.objectives.means') : t('drilldown.objectives.recommendedActions')}
                              items={meansOrActions}
                              tone="muted"
                            />
                          )}
                          {motivations.length > 0 && (
                            <ObjectivesList
                              label={t('drilldown.objectives.motivations')}
                              items={motivations}
                              tone="muted"
                            />
                          )}
                        </ObjectifSection>
                      )}
                    </div>
                  );
                })()}
                {evaluations.length > 0 && (() => {
                  const maxEvalCount = Math.max(...evaluations.map(e => e.eval_count));
                  return (
                    <div className="eval-section">
                      <div className="section-label" style={{ marginBottom: 'var(--s4)' }}>
                        {t('drilldown.evaluations.title')} <span className="eval-section__count">({maxEvalCount})</span>
                      </div>
                      <div className="flex flex-col gap-4">
                        {evaluations.map(ev => (
                          <div key={ev.code} className="eval-bar">
                            <span className="eval-bar__label">{ev.label}</span>
                            <div className="eval-bar__track">
                              <div className="eval-bar__fill" style={{ width: `${(ev.avg_score / 5) * 100}%` }} />
                            </div>
                            <span className="eval-bar__score">{ev.avg_score}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Informations personnelles */}
              <div className="sidebar-card">
                <div className="sidebar-card__header">{t('drilldown.personalInfo.title')}</div>
                <div className="info-row">
                  <span className="info-row__label">{t('drilldown.personalInfo.birth')}</span>
                  <span className="info-row__value">
                    {formatDate(person.date_of_birth)}{computeAge(person.date_of_birth)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-row__label">{t('drilldown.personalInfo.email')}</span>
                  <span className="info-row__value">
                    {person.email ? <a href={`mailto:${person.email}`}>{person.email}</a> : '—'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-row__label">{t('drilldown.personalInfo.phone')}</span>
                  <span className="info-row__value">{person.phone || person.mobile || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-row__label">{t('drilldown.personalInfo.address')}</span>
                  <span className="info-row__value">{[person.address, person.postal_code, person.city].filter(Boolean).join(', ') || '—'}</span>
                </div>
              </div>
            </div>

            {/* BSP — right column */}
            {hasBspOrIntake && (
              <div className="bsp-card">
                <div className="bsp-card__header">{t('drilldown.bsp.title')}</div>
                <div className="bsp-card__body">
                  {bsp && (
                    <>
                      <div className="bsp-field-row">
                        <span className="bsp-field-row__icon">—</span>
                        <span className="bsp-field-row__label">{t('drilldown.bsp.date')}</span>
                        <span className="bsp-field-row__value">{formatDate(bsp.bsp_date)}</span>
                      </div>
                      <div className="bsp-field-row">
                        <span className="bsp-field-row__icon">—</span>
                        <span className="bsp-field-row__label">{t('drilldown.bsp.location')}</span>
                        <span className="bsp-field-row__value">{bsp.location || '—'}</span>
                      </div>
                      <div className="bsp-field-row">
                        <span className="bsp-field-row__icon">✓</span>
                        <span className="bsp-field-row__label">{t('drilldown.bsp.charterSigned')}</span>
                        <span className="bsp-field-row__value">{bsp.charter_signed ? t('common.yes') : t('common.no')}</span>
                      </div>
                      <div className="bsp-field-row">
                        <span className="bsp-field-row__icon">—</span>
                        <span className="bsp-field-row__label">{t('drilldown.bsp.referent')}</span>
                        <span className="bsp-field-row__value">{bsp.referent_name || '—'}</span>
                      </div>
                    </>
                  )}
                  {intake?.note && (
                    <div className="bsp-field-row">
                      <span className="bsp-field-row__icon">◆</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.intakeNote')}</span>
                      <span className="bsp-field-row__value">{intake.note}</span>
                    </div>
                  )}
                  {intake?.health?.situation_note && (
                    <div className="bsp-field-row">
                      <span className="bsp-field-row__icon">◐</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.intakeSituation')}</span>
                      <span className="bsp-field-row__value">{intake.health.situation_note}</span>
                    </div>
                  )}
                  {intake?.health?.health_status && (
                    <div className="bsp-field-row bsp-field-row--warn">
                      <span className="bsp-field-row__icon">♡</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.health')}</span>
                      <span className="bsp-field-row__value">{intake.health.health_status}</span>
                    </div>
                  )}
                  {intake?.health?.medications && (
                    <div className="bsp-field-row bsp-field-row--warn">
                      <span className="bsp-field-row__icon">⚕</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.medication')}</span>
                      <span className="bsp-field-row__value">{intake.health.medications}</span>
                    </div>
                  )}
                  {intake?.health?.doctor_name && (
                    <div className="bsp-field-row">
                      <span className="bsp-field-row__icon">—</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.doctor')}</span>
                      <span className="bsp-field-row__value">{intake.health.doctor_name}</span>
                    </div>
                  )}
                  {bsp?.notes && (
                    <div className="bsp-field-row">
                      <span className="bsp-field-row__icon">—</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.notes')}</span>
                      <span className="bsp-field-row__value" style={{ whiteSpace: 'pre-wrap' }}>{cleanHtml(bsp.notes)}</span>
                    </div>
                  )}
                  {bsp?.objectives_set && (
                    <div className="bsp-field-row">
                      <span className="bsp-field-row__icon">◎</span>
                      <span className="bsp-field-row__label">{t('drilldown.bsp.bspObjectives')}</span>
                      <span className="bsp-field-row__value" style={{ whiteSpace: 'pre-wrap' }}>{bsp.objectives_set}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── BSP context (forces, limitations, projets, etc.) — objectifs are in the Objectifs panel above ── */}
          {bspDoc && bspDoc.extracted_fields && (
            <section style={{ marginTop: 'var(--s16)', padding: 'var(--s12)', border: '1px solid var(--line)', borderRadius: 'var(--r1)', background: 'var(--surface)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--s8)' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                  {t('drilldown.bspDetail.title')}
                </h3>
                <a
                  href={api.documentBlobUrl(bspDoc.document_id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}
                >
                  {t('drilldown.bspDetail.viewSource')}
                </a>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--s8)', marginTop: 'var(--s8)', fontSize: 'var(--t-12)' }}>
                {(() => {
                  const f: any = bspDoc.extracted_fields;
                  const listToText = (v: unknown): string | null =>
                    Array.isArray(v) ? (v.length > 0 ? v.join(' · ') : null) : (typeof v === 'string' && v.trim() ? v : null);
                  const entries: Array<[string, string | null]> = [
                    [t('drilldown.bspDetail.strengths'), listToText(f.forces_ressources) ?? listToText(f.observed_strengths)],
                    [t('drilldown.bspDetail.limitations'), listToText(f.limitations) ?? listToText(f.observed_limitations)],
                    [t('drilldown.bspDetail.professionalDirection'), f.professional_direction ?? null],
                    [t('drilldown.bspDetail.alternativePaths'), listToText(f.alternative_directions)],
                    [t('drilldown.bspDetail.projectIdeas'), f.idees_projets_pro ?? null],
                    [t('drilldown.bspDetail.desiredRate'), f.taux_souhaite_exigible ?? null],
                    [t('drilldown.bspDetail.lastProfession'), f.derniere_profession ?? null],
                    [t('drilldown.bspDetail.trainings'), f.formations ?? null],
                    [t('drilldown.bspDetail.languagesIt'), [f.langues, f.informatique].filter(Boolean).join(' · ') || null],
                    [t('drilldown.bspDetail.availabilities'), f.disponibilites ?? null],
                  ];
                  return entries.filter(([, v]) => v).map(([label, value]) => (
                    <div key={String(label)}>
                      <div style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {label}
                      </div>
                      <div style={{ color: 'var(--ink-1)', whiteSpace: 'pre-wrap' }}>{String(value)}</div>
                    </div>
                  ));
                })()}
              </div>
            </section>
          )}

          {/* ── Documents — mesure active ── */}
          {docsLoaded && (docsCurrent.length > 0 || docsUnattributed.length > 0) && (
            <section style={{ marginTop: 'var(--s16)' }}>
              <h3 style={{ margin: '0 0 var(--s8)', fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                {t('drilldown.documents.title')}{activeMeasure ? ` — ${activeMeasure.subtype_name || activeMeasure.type_label}` : ''}
                <span style={{ marginLeft: 'var(--s8)', color: 'var(--ink-3)', fontWeight: 400 }}>
                  ({t('drilldown.documents.attributedCount', { n: docsCurrent.length, s: docsCurrent.length !== 1 ? 's' : '' })}
                  {docsUnattributed.length > 0 ? ` · ${t('drilldown.documents.unattributedCount', { n: docsUnattributed.length, s: docsUnattributed.length !== 1 ? 's' : '' })}` : ''})
                </span>
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 'var(--s8)' }}>
                {docsCurrent.map((d) => (
                  <DocumentCard key={d.id} doc={d} onOpen={setPreviewDoc} />
                ))}
                {docsUnattributed.map((d) => (
                  <DocumentCard key={d.id} doc={d} onOpen={setPreviewDoc} />
                ))}
              </div>
            </section>
          )}

          {/* ── Fichiers de la base (file_attachment, served on-demand over SFTP) ── */}
          {files.length > 0 && (
            <section style={{ marginTop: 'var(--s16)' }}>
              <h3 style={{ margin: '0 0 var(--s8)', fontSize: 'var(--t-13)', color: 'var(--ink-2)' }}>
                {t('drilldown.documents.files')}
                <span style={{ marginLeft: 'var(--s8)', color: 'var(--ink-3)', fontWeight: 400 }}>
                  ({files.length})
                </span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s4)' }}>
                {files.map((f: any) => (
                  <a
                    key={f.id}
                    href={api.participantFileUrl(participantId, f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--s8)',
                      padding: 'var(--s8) var(--s12)', border: '1px solid var(--line)',
                      borderRadius: 'var(--r1)', backgroundColor: 'var(--surface)',
                      textDecoration: 'none', color: 'var(--ink-1)',
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 'var(--t-13)' }}>📄</span>
                    <span style={{ flex: 1, fontSize: 'var(--t-12)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.original_filename}
                    </span>
                    {f.is_mandate && (
                      <span className="badge badge-sm" title={t('drilldown.documents.mandateTitle')}>{t('drilldown.documents.mandate')}</span>
                    )}
                    {f.source === 'intake' && (
                      <span className="badge badge-sm" style={{ opacity: 0.7 }}>{t('drilldown.documents.intake')}</span>
                    )}
                    <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                      {f.size_bytes ? t('drilldown.documents.sizeKo', { n: Math.round(f.size_bytes / 1024) }) : ''}
                    </span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
        );
      })()}

      {/* ── Tab: Mesures ── */}
      {tab === 'measures' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
          {measuresData.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 'var(--t-13)' }}>{t('drilldown.measuresTab.none')}</p>
          ) : (
            measuresData.map((m: any) => (
              <div key={m.id} className="measure-card" style={{
                border: m.is_current ? '1.5px solid var(--accent)' : '1px solid var(--line)',
                borderRadius: 'var(--r1)',
                backgroundColor: 'var(--surface)',
                overflow: 'hidden',
              }}>
                {/* Header bar */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: 'var(--s8) var(--s12)',
                  backgroundColor: m.is_current ? 'var(--accent-dim)' : 'var(--bg)',
                  borderBottom: '1px solid var(--line)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 'var(--t-14)' }}>{m.subtype_name || m.type_label}</span>
                    <span className={`badge badge-sm ${m.is_current ? 'badge-ok' : ''}`}>
                      {m.is_current ? t('drilldown.measuresTab.current') : m.status_label}
                    </span>
                    {m.contract_cout_chf != null && m.contract_cout_chf > 0 ? (
                      <span
                        className="badge badge-sm"
                        title={t('drilldown.measuresTab.contractCostTitle', {
                          n: m.contract_n_contrat ? ` ${m.contract_n_contrat}` : '',
                          type: m.contract_type_contrat ? ` (${m.contract_type_contrat})` : '',
                          months: m.contract_duree_mois ? ` · ${t('drilldown.measuresTab.monthsCount', { n: m.contract_duree_mois })}` : '',
                        })}
                        style={{ fontWeight: 600 }}
                      >
                        {t('drilldown.measuresTab.contractCost', { amount: Number(m.contract_cout_chf).toLocaleString('fr-CH', { maximumFractionDigits: 0 }) })}
                      </span>
                    ) : m.subtype_price != null && Number(m.subtype_price) > 0 ? (
                      <span className="badge badge-sm" title={t('drilldown.measuresTab.catalogPriceTitle')}>
                        {t('drilldown.measuresTab.catalogPrice', { amount: Number(m.subtype_price).toFixed(0) })}
                      </span>
                    ) : null}
                    {m.default_months && (
                      <span className="badge badge-sm">{t('drilldown.measuresTab.monthsPlanned', { n: m.default_months })}</span>
                    )}
                    {m.oai_position && (
                      <span className="badge badge-sm" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-10)' }}>{t('drilldown.measuresTab.article', { n: m.oai_position })}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>
                    {formatDate(m.start_date)} – {formatDate(m.end_date)}
                    {m.is_current && m.end_date && (
                      <span style={{ marginLeft: 'var(--s4)' }}>
                        ({t('drilldown.measuresTab.daysRemaining', { n: Math.max(0, Math.ceil((new Date(m.end_date).getTime() - Date.now()) / (1000*60*60*24))) })})
                      </span>
                    )}
                  </span>
                </div>

                {/* Body */}
                <div style={{ padding: 'var(--s12)' }}>
                  {/* Info row */}
                  <div style={{ display: 'flex', gap: 'var(--s24)', marginBottom: 'var(--s8)', flexWrap: 'wrap', fontSize: 'var(--t-12)' }}>
                    <div>
                      <span style={{ color: 'var(--ink-3)' }}>{t('drilldown.measuresTab.mandant')} </span>
                      <span style={{ fontWeight: 500 }}>{m.mandant_name || '—'}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--ink-3)' }}>{t('drilldown.measuresTab.advisor')} </span>
                      <span style={{ fontWeight: 500 }}>
                        {m.contact_first_name ? `${m.contact_first_name} ${m.contact_last_name}` : '—'}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--ink-3)' }}>RM : </span>
                      <span style={{ fontWeight: 500 }}>{m.rm_name || '—'}</span>
                    </div>
                    {m.contract_number && (
                      <div>
                        <span style={{ color: 'var(--ink-3)' }}>{t('drilldown.measuresTab.contract')} </span>
                        <span style={{ fontWeight: 500, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-11)' }}>{m.contract_number}</span>
                      </div>
                    )}
                  </div>

                  {/* Metric pills */}
                  <div style={{ display: 'flex', gap: 'var(--s4)', marginBottom: 'var(--s8)', flexWrap: 'wrap' }}>
                    {m.presence_rate !== null && (
                      <span className="stat-pill">{t('drilldown.measuresTab.presencePct', { pct: m.presence_rate })}</span>
                    )}
                    <span className="stat-pill">{t('drilldown.measuresTab.perWeek', { hours: fmtH(m.hours_per_week) })}</span>
                    <span className="stat-pill">{t('drilldown.measuresTab.totalHours', { hours: fmtH(m.total_hours) })}</span>
                    <span className="stat-pill">{t('drilldown.measuresTab.sessionsPerWeek', { sessions: m.present_sessions, weeks: m.effective_weeks || m.weeks })}</span>
                    {m.vacation_days > 0 && (
                      <span className="stat-pill">{t('drilldown.measuresTab.vacationDays', { n: m.vacation_days })}</span>
                    )}
                    <span className="stat-pill">{t('drilldown.measuresTab.journalCount', { n: m.journal_count })}</span>
                    <span className="stat-pill">{t('drilldown.measuresTab.reportCount', { n: m.report_count })}</span>
                  </div>

                  {/* Goals */}
                  {m.goals.length > 0 && (
                    <div style={{ marginBottom: 'var(--s8)' }}>
                      <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500 }}>{t('drilldown.measuresTab.objectives')} </span>
                      {m.goals.map((g: string, i: number) => (
                        <span key={i} className="badge badge-sm" style={{ marginRight: 'var(--s4)' }}>{g}</span>
                      ))}
                    </div>
                  )}

                  {/* Effective progression plan (OAI decision OR RM adjustment) attributed to this mesure */}
                  {(() => {
                    const plan = plansByMeasure[String(m.id)];
                    if (!plan) return null;
                    const targetLabel = plan.progression_type === 'progressive'
                      ? t('drilldown.measuresTab.rateRange', { start: plan.start_pct, target: plan.target_pct })
                      : t('drilldown.measuresTab.rateConstant', { target: plan.target_pct });
                    const sourceLabel = plan.source === 'rm_adjustment' ? t('drilldown.objectives.rmAdjustment') : t('drilldown.objectives.aiMandate');
                    const objs = Array.isArray(plan.qualitative_objectives) ? plan.qualitative_objectives : [];
                    return (
                      <div style={{ marginBottom: 'var(--s8)', padding: 'var(--s8)', background: 'var(--bg)', borderLeft: '3px solid var(--accent)', borderRadius: 'var(--r1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--s4)', marginBottom: objs.length > 0 ? 'var(--s4)' : 0 }}>
                          <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)', fontWeight: 600 }}>
                            <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{sourceLabel} : </span>
                            {targetLabel}
                            {plan.target_date && (
                              <span style={{ marginLeft: 'var(--s6)', color: 'var(--ink-3)', fontWeight: 400 }}>
                                {t('drilldown.measuresTab.byDate', { date: formatDate(plan.target_date) })}
                              </span>
                            )}
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s8)' }}>
                            {plan.oai_source_document_id && plan.source === 'oai_decision' && (
                              <a
                                href={api.documentBlobUrl(plan.oai_source_document_id)}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)' }}
                              >
                                {t('drilldown.measuresTab.sourcePdf')}
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => setAdjustingMeasureId(m.id)}
                              style={{
                                padding: '2px 8px',
                                fontSize: 'var(--t-11)',
                                fontWeight: 600,
                                borderRadius: 'var(--r2)',
                                border: '1px solid var(--accent)',
                                background: 'var(--surface)',
                                color: 'var(--accent)',
                                cursor: 'pointer',
                              }}
                            >
                              {t('drilldown.objectives.adjustAction')}
                            </button>
                          </span>
                        </div>
                        {objs.length > 0 && (
                          <ul style={{ margin: 0, paddingLeft: 'var(--s16)', fontSize: 'var(--t-11)', color: 'var(--ink-1)' }}>
                            {objs.map((o, i) => <li key={i}>{o}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  })()}

                  {/* Historique du plan — OAI mandat + ajustements RM */}
                  {(() => {
                    const planForM = plansByMeasure[String(m.id)];
                    const oaiSeg = planForM?.timeline?.find(s => s.source === 'oai_decision') ?? null;
                    return (
                      <AdjustmentHistory
                        measureId={m.id}
                        version={planVersion}
                        oai={oaiSeg ? {
                          source_document_id: oaiSeg.oai_source_document_id ?? null,
                          progression_type: oaiSeg.progression_type,
                          start_pct: oaiSeg.start_pct,
                          target_pct: oaiSeg.target_pct,
                          period_start: oaiSeg.period_start,
                          target_date: oaiSeg.target_date,
                          source_created_at: oaiSeg.source_created_at,
                        } : null}
                        onChange={() => {
                          setPlanVersion(v => v + 1);
                          setCommsRefresh(v => v + 1);
                        }}
                      />
                    );
                  })()}

                  {/* Ateliers breakdown */}
                  {m.ateliers.length > 0 && (
                    <div>
                      <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500, display: 'block', marginBottom: 'var(--s4)' }}>{t('drilldown.measuresTab.workshops')}</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s4)' }}>
                        {m.ateliers.map((a: any, i: number) => (
                          <span key={i} className="stat-pill">
                            {t('drilldown.measuresTab.workshopStat', { activity: a.activity, sessions: a.sessions, hours: fmtH(a.hours) })}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents attribués à cette mesure */}
                  {(docsByMeasure[String(m.id)] || []).length > 0 && (
                    <div style={{ marginTop: 'var(--s12)', paddingTop: 'var(--s12)', borderTop: '1px dashed var(--line)' }}>
                      <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500, display: 'block', marginBottom: 'var(--s8)' }}>
                        {t('drilldown.measuresTab.documentsCount', { n: (docsByMeasure[String(m.id)] || []).length })}
                      </span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 'var(--s8)' }}>
                        {(docsByMeasure[String(m.id)] || []).map((d) => (
                          <DocumentCard key={d.id} doc={d} onOpen={setPreviewDoc} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Tab: Communications ── */}
      {tab === 'communications' && (
        <CommunicationsPanel
          participantId={participantId}
          refreshSignal={commsRefresh}
          onUnsentCount={setUnsentComms}
        />
      )}

      {/* ── Tab: Attendance & Activities ── */}
      {tab === 'attendance' && (() => {
        const DOW_FR: Record<number, string> = {
          1: t('drilldown.dow.mon'), 2: t('drilldown.dow.tue'), 3: t('drilldown.dow.wed'),
          4: t('drilldown.dow.thu'), 5: t('drilldown.dow.fri'), 6: t('drilldown.dow.sat'), 7: t('drilldown.dow.sun'),
        };
        const totalSessions = ateliers.reduce((s: number, a: any) => s + a.session_count, 0);
        const totalHours = ateliers.reduce((s: number, a: any) => s + Number(a.total_hours), 0);

        // Planned ateliers: scoped to the active mesure when possible.
        const plannedAteliers = activeMeasure
          ? enrollments.filter((e: any) => e.measure_id === activeMeasure.id)
          : enrollments;
        const plannedWeeklyHours = plannedAteliers.reduce((s: number, e: any) => {
          if (!e.start_time || !e.end_time) return s;
          const [sh, sm] = String(e.start_time).split(':').map(Number);
          const [eh, em] = String(e.end_time).split(':').map(Number);
          return s + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
        }, 0);
        const weeksSpan = attStats?.firstAttendance && attStats?.lastAttendance
          ? Math.max(1, Math.round((new Date(attStats.lastAttendance).getTime() - new Date(attStats.firstAttendance).getTime()) / (7 * 24 * 60 * 60 * 1000)))
          : null;
        const avgHpw = weeksSpan ? fmtH(Math.round((totalHours / weeksSpan) * 10) / 10) : '—';

        // Mesure boundary: a month is "in active mesure" when it overlaps the
        // mesure's date range. Used to draw a delineation row between current
        // and past mesures in Résumé mensuel and Absences below.
        const activeMesureStartTs = activeMeasure?.start_date ? new Date(activeMeasure.start_date + 'T00:00:00Z').getTime() : null;
        const activeMesureEndTs = activeMeasure?.end_date
          ? new Date(activeMeasure.end_date + 'T00:00:00Z').getTime()
          : (activeMesureStartTs != null ? Number.POSITIVE_INFINITY : null);
        const monthInActive = (yyyymm: string): boolean => {
          if (activeMesureStartTs == null || activeMesureEndTs == null) return false;
          const [y, mo] = yyyymm.split('-').map(Number);
          const monStart = Date.UTC(y, mo - 1, 1);
          const monEnd = Date.UTC(y, mo, 0); // last day of month
          return monStart <= activeMesureEndTs && monEnd >= activeMesureStartTs;
        };
        const dateInActive = (iso: string | null | undefined): boolean => {
          if (!iso || activeMesureStartTs == null || activeMesureEndTs == null) return false;
          const t = new Date(iso.slice(0, 10) + 'T00:00:00Z').getTime();
          return t >= activeMesureStartTs && t <= activeMesureEndTs;
        };

        // Flatten monthly summary: one row per activity per month, with month index for alternating bg
        const monthlyRows: Array<{ month: string; activity: string; session: string | null; sessions: number; hours: number; isFirstInMonth: boolean; monthRowSpan: number; monthIdx: number; rowInMonth: number; inActiveMesure: boolean }> = [];
        monthlySummary.forEach((m, mi) => {
          const acts = m.activities || [];
          const inAct = monthInActive(m.month);
          if (acts.length === 0) {
            monthlyRows.push({ month: m.month, activity: '—', session: null, sessions: m.present_days, hours: m.total_hours, isFirstInMonth: true, monthRowSpan: 1, monthIdx: mi, rowInMonth: 0, inActiveMesure: inAct });
          } else {
            acts.forEach((a: any, ai: number) => {
              monthlyRows.push({ month: m.month, activity: a.name, session: a.session ?? null, sessions: a.sessions, hours: a.hours, isFirstInMonth: ai === 0, monthRowSpan: acts.length, monthIdx: mi, rowInMonth: ai, inActiveMesure: inAct });
            });
          }
        });
        // Find the index of the first row that transitions out of the active mesure.
        // monthlyRows are ordered newest first (DESC) and within month are activity rows.
        let monthlyBoundaryIdx = -1;
        for (let i = 0; i < monthlyRows.length; i++) {
          if (monthlyRows[i].isFirstInMonth && !monthlyRows[i].inActiveMesure) {
            // Only insert a boundary if there was at least one active-mesure row above.
            if (monthlyRows.slice(0, i).some(r => r.inActiveMesure)) {
              monthlyBoundaryIdx = i;
            }
            break;
          }
        }

        return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s16)' }}>
          {/* Section 1: AI Attendance Analysis */}
          <AIPanel
            title={t('drilldown.attendance.aiTitle')}
            buttonLabel={t('drilldown.attendance.aiButton')}
            withDateRange
            hint={t('drilldown.attendance.aiPeriodHint')}
            onGenerate={async (range) => {
              const result = await api.attendanceAnalysis(participantId, measureId, range) as any;
              return result.analysis;
            }}
          />

          {/* Section 2: Heatmap */}
          <section>
            <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, marginBottom: 'var(--s4)', color: 'var(--ink-1)' }}>{t('drilldown.attendance.heatmapTitle')}</h3>
            <AttendanceHeatmap data={attendance} />
          </section>

          {/* Section: Rendez-vous (recent history) */}
          {appointmentsRecent.length > 0 && (
            <section>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s4)', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.attendance.appointments')}</h3>
                <div style={{ display: 'flex', gap: 'var(--s4)', flexWrap: 'wrap' }}>
                  {appointmentCounts.map((c: any) => (
                    <span key={c.type_code} className="stat-pill">{c.count} {c.type_name}</span>
                  ))}
                </div>
              </div>
              <table className="table table-compact" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>{t('drilldown.attendance.colDate')}</th>
                    <th>{t('drilldown.attendance.colType')}</th>
                    <th>{t('drilldown.attendance.colSchedule')}</th>
                    <th>{t('drilldown.attendance.colStaff')}</th>
                    <th>{t('drilldown.attendance.colComment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {appointmentsRecent.map((a: any, i: number) => {
                    const hasComment = !!(a.comment && String(a.comment).trim().length > 0);
                    return (
                      <tr
                        key={a.id}
                        onClick={hasComment ? () => setPreviewAppointment(a as AppointmentDetail) : undefined}
                        title={hasComment ? t('drilldown.attendance.viewDetail') : undefined}
                        style={{
                          backgroundColor: i % 2 === 1 ? 'var(--bg-inset)' : 'var(--surface)',
                          cursor: hasComment ? 'pointer' : 'default',
                        }}
                      >
                        <td style={{ fontWeight: 500 }}>{formatDate(a.appointment_date)}</td>
                        <td><span className="badge badge-sm">{a.type_name}</span></td>
                        <td style={{ color: 'var(--ink-2)' }}>
                          {a.start_time ? `${a.start_time.slice(0, 5)}${a.end_time ? `–${a.end_time.slice(0, 5)}` : ''}` : '—'}
                        </td>
                        <td>{a.staff_name || '—'}</td>
                        <td style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {hasComment ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {a.comment}
                              <span style={{ color: 'var(--accent)', fontSize: 'var(--t-10)', flexShrink: 0 }}>›</span>
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {/* Section: Ateliers planifiés — current enrollments for the active mesure */}
          {plannedAteliers.length > 0 && (
            <section>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s4)', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>
                  {t('drilldown.attendance.plannedWorkshops')}{activeMeasure ? ` · ${activeMeasure.subtype_name || activeMeasure.type_label || t('drilldown.attendance.currentMeasure')}` : ''}
                </h3>
                <div style={{ display: 'flex', gap: 'var(--s4)', flexWrap: 'wrap' }}>
                  <span className="stat-pill">{t('drilldown.attendance.sessionsPerWeek', { n: plannedAteliers.length, s: plannedAteliers.length > 1 ? 's' : '' })}</span>
                  <span className="stat-pill">{t('drilldown.measuresTab.perWeek', { hours: fmtH(Math.round(plannedWeeklyHours * 10) / 10) })}</span>
                </div>
              </div>
              <table className="table table-compact" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>{t('drilldown.attendance.colActivity')}</th>
                    <th>{t('drilldown.attendance.colSession')}</th>
                    <th>{t('drilldown.attendance.colDay')}</th>
                    <th>{t('drilldown.attendance.colSchedule')}</th>
                    <th>{t('drilldown.attendance.colPeriod')}</th>
                  </tr>
                </thead>
                <tbody>
                  {plannedAteliers.map((e: any, i: number) => (
                    <tr key={e.enrollment_id} style={{ backgroundColor: i % 2 === 1 ? 'var(--bg-inset)' : 'var(--surface)' }}>
                      <td style={{ fontWeight: 500 }}>{e.activity_name}</td>
                      <td style={{ color: 'var(--ink-2)' }}>{e.session_name || '—'}</td>
                      <td>{DOW_FR[e.day_of_week] || '—'}</td>
                      <td>{e.start_time?.slice(0, 5)}{e.end_time ? `–${e.end_time.slice(0, 5)}` : ''}</td>
                      <td style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>
                        {e.enrollment_start ? formatDate(e.enrollment_start) : '—'}
                        {e.enrollment_end ? ` → ${formatDate(e.enrollment_end)}` : ` → ${t('drilldown.attendance.ongoing')}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Section 3: Ateliers suivis — title row with stat pills, grouped by category */}
          {ateliers.length > 0 && (() => {
            // Group ateliers by category
            const categoryMap = new Map<string, any[]>();
            for (const a of ateliers) {
              const cat = a.category_name || t('drilldown.attendance.otherCategory');
              const list = categoryMap.get(cat) || [];
              list.push(a);
              categoryMap.set(cat, list);
            }
            const categories = Array.from(categoryMap.entries());

            return (
            <section>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s4)', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.attendance.workshopsAttended')}</h3>
                <div style={{ display: 'flex', gap: 'var(--s4)', flexWrap: 'wrap' }}>
                  {attStats && (
                    <span className="stat-pill">{t('drilldown.attendance.presenceStat', { value: attStats.presenceRate != null ? `${attStats.presenceRate} %` : '—' })}</span>
                  )}
                  <span className="stat-pill">{t('drilldown.measuresTab.perWeek', { hours: avgHpw })}</span>
                  {attStats && (
                    <span className="stat-pill">{t('drilldown.attendance.daysCount', { n: attStats.presentCount })}</span>
                  )}
                </div>
              </div>
              {(() => {
                const totalCurrentSessions = ateliers.reduce((s: number, a: any) => s + Number(a.current_session_count || 0), 0);
                const totalCurrentHours = ateliers.reduce((s: number, a: any) => s + Number(a.current_total_hours || 0), 0);
                const hasCurrent = totalCurrentSessions > 0;
                return (
              <table className="table table-compact" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>{t('drilldown.attendance.colActivity')}</th>
                    <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>{t('drilldown.attendance.colSession')}</th>
                    <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>{t('drilldown.attendance.colDay')}</th>
                    <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>{t('drilldown.attendance.colSchedule')}</th>
                    <th colSpan={2} style={{ textAlign: 'center', borderBottom: '1px solid var(--line)', color: hasCurrent ? 'var(--accent)' : 'var(--ink-3)', fontWeight: 600 }}>
                      {t('drilldown.attendance.currentMeasureCol')}
                    </th>
                    <th colSpan={2} style={{ textAlign: 'center', borderBottom: '1px solid var(--line)', color: 'var(--ink-3)', fontWeight: 600 }}>
                      {t('drilldown.attendance.allMeasuresCol')}
                    </th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colSessions')}</th>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colHours')}</th>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colSessions')}</th>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colHours')}</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(([catName, catAteliers], ci) => {
                    const catSessions = catAteliers.reduce((s: number, a: any) => s + a.session_count, 0);
                    const catHours = catAteliers.reduce((s: number, a: any) => s + Number(a.total_hours), 0);
                    const catCurrentSessions = catAteliers.reduce((s: number, a: any) => s + Number(a.current_session_count || 0), 0);
                    const catCurrentHours = catAteliers.reduce((s: number, a: any) => s + Number(a.current_total_hours || 0), 0);
                    return (
                      <Fragment key={catName}>
                        {/* Category header row */}
                        <tr style={{ borderTop: ci > 0 ? '2px solid var(--line-strong)' : undefined }}>
                          <td colSpan={4} style={{ fontWeight: 700, fontSize: 'var(--t-12)', color: 'var(--ink-1)', backgroundColor: 'var(--bg)', paddingTop: 'var(--s4)', paddingBottom: 'var(--s4)' }}>
                            {catName}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 'var(--t-11)', color: catCurrentSessions > 0 ? 'var(--accent)' : 'var(--ink-3)', backgroundColor: 'var(--bg)' }}>{catCurrentSessions || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 'var(--t-11)', color: catCurrentSessions > 0 ? 'var(--accent)' : 'var(--ink-3)', backgroundColor: 'var(--bg)' }}>{catCurrentSessions > 0 ? fmtH(catCurrentHours) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 'var(--t-11)', color: 'var(--ink-3)', backgroundColor: 'var(--bg)' }}>{catSessions}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 'var(--t-11)', color: 'var(--ink-3)', backgroundColor: 'var(--bg)' }}>{fmtH(catHours)}</td>
                        </tr>
                        {catAteliers.map((a: any, i: number) => {
                          const cur = Number(a.current_session_count || 0);
                          const curH = Number(a.current_total_hours || 0);
                          return (
                          <tr key={i} style={{ backgroundColor: i % 2 === 1 ? 'var(--bg-inset)' : 'var(--surface)' }}>
                            <td style={{ paddingLeft: 'var(--s12)' }}>{a.activity_name}</td>
                            <td style={{ color: 'var(--ink-2)' }}>{a.session_name || '—'}</td>
                            <td>{DOW_FR[a.day_of_week] || '—'}</td>
                            <td>{a.start_time?.slice(0, 5)}–{a.end_time?.slice(0, 5)}</td>
                            <td style={{ textAlign: 'right', color: cur > 0 ? 'var(--accent)' : 'var(--ink-3)', fontWeight: cur > 0 ? 600 : 400 }}>{cur > 0 ? cur : '—'}</td>
                            <td style={{ textAlign: 'right', color: cur > 0 ? 'var(--accent)' : 'var(--ink-3)', fontWeight: cur > 0 ? 600 : 400 }}>{cur > 0 ? fmtH(curH) : '—'}</td>
                            <td style={{ textAlign: 'right' }}>{a.session_count}</td>
                            <td style={{ textAlign: 'right' }}>{fmtH(Number(a.total_hours))}</td>
                          </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ fontWeight: 600, borderTop: '2px solid var(--line-strong)' }}>{t('drilldown.attendance.total')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, borderTop: '2px solid var(--line-strong)', color: hasCurrent ? 'var(--accent)' : 'var(--ink-3)' }}>{hasCurrent ? totalCurrentSessions : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, borderTop: '2px solid var(--line-strong)', color: hasCurrent ? 'var(--accent)' : 'var(--ink-3)' }}>{hasCurrent ? fmtH(totalCurrentHours) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, borderTop: '2px solid var(--line-strong)' }}>{totalSessions}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, borderTop: '2px solid var(--line-strong)' }}>{fmtH(totalHours)}</td>
                  </tr>
                </tfoot>
              </table>
                );
              })()}
            </section>
            );
          })()}

          {/* Section 4: Stages externes (placement timesheets) */}
          {placements && (
            <section>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s4)', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.attendance.externalPlacements')}</h3>
                <div style={{ display: 'flex', gap: 'var(--s4)', flexWrap: 'wrap' }}>
                  <span className="stat-pill">{t('drilldown.attendance.daysTotal', { n: placements.total_days })}</span>
                  <span className="stat-pill">{t('drilldown.measuresTab.totalHours', { hours: fmtH(Math.round(placements.total_hours * 10) / 10) })}</span>
                  <span className="stat-pill">{t('drilldown.attendance.daysLast3mo', { n: placements.days_3mo })}</span>
                </div>
              </div>
            </section>
          )}

          {/* Section 5: Monthly Summary — white rows, strong month separators */}
          {monthlySummary.length > 0 && (
            <section>
              <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, marginBottom: 'var(--s4)', color: 'var(--ink-1)' }}>{t('drilldown.attendance.monthlySummary')}</h3>
              <table className="table table-compact" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th>{t('drilldown.attendance.colMonth')}</th>
                    <th>{t('drilldown.attendance.colWorkshop')}</th>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colAttendances')}</th>
                    <th style={{ textAlign: 'right' }}>{t('drilldown.attendance.colHours')}</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyRows.map((r, i) => {
                    const isOddRow = r.rowInMonth % 2 === 1;
                    const boundary = i === monthlyBoundaryIdx;
                    return (
                      <Fragment key={i}>
                        {boundary && (
                          <tr>
                            <td colSpan={4} style={{
                              padding: 'var(--s4) var(--s8)',
                              fontSize: 'var(--t-10)',
                              fontWeight: 600,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: 'var(--ink-3)',
                              background: 'var(--bg)',
                              borderTop: '2px solid var(--accent)',
                              borderBottom: '1px solid var(--line)',
                            }}>
                              {t('drilldown.attendance.previousMeasures')}
                            </td>
                          </tr>
                        )}
                        <tr
                          style={{
                            backgroundColor: isOddRow ? 'var(--bg-inset)' : 'var(--surface)',
                            borderTop: r.isFirstInMonth && i > 0 && !boundary ? '2px solid var(--line-strong)' : undefined,
                          }}
                        >
                          {r.isFirstInMonth ? (
                            <td rowSpan={r.monthRowSpan} style={{ verticalAlign: 'top', fontWeight: 600, backgroundColor: 'var(--surface)', borderRight: '1px solid var(--line)' }}>{r.month}</td>
                          ) : null}
                          <td style={{ color: 'var(--ink-2)' }}>
                            {r.activity}
                            {r.session && r.session !== r.activity && (
                              <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}> · {r.session}</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>{r.sessions}</td>
                          <td style={{ textAlign: 'right' }}>{fmtH(r.hours)}</td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {/* Section 4: Absences formelles */}
          {(() => {
            // Find the first absence row that's NOT in the active mesure window.
            // Absences are ordered DESC by start_date (newest first). The boundary
            // is the index of the first non-active row, provided at least one
            // active-mesure absence appears above it.
            let absenceBoundaryIdx = -1;
            for (let i = 0; i < absences.length; i++) {
              const inAct = dateInActive(absences[i].start_date) || dateInActive(absences[i].end_date);
              if (!inAct) {
                if (absences.slice(0, i).some((a: any) => dateInActive(a.start_date) || dateInActive(a.end_date))) {
                  absenceBoundaryIdx = i;
                }
                break;
              }
            }
            return (
          <section>
            <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, marginBottom: 'var(--s4)', color: 'var(--ink-1)' }}>{t('drilldown.attendance.formalAbsences')}</h3>
            {absences.length === 0 ? (
              <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('drilldown.attendance.noAbsences')}</p>
            ) : (
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>{t('drilldown.attendance.colType')}</th>
                    <th>{t('drilldown.attendance.colStart')}</th>
                    <th>{t('drilldown.attendance.colEnd')}</th>
                    <th>{t('drilldown.attendance.colComment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.map((a: any, i: number) => (
                    <Fragment key={i}>
                      {i === absenceBoundaryIdx && (
                        <tr>
                          <td colSpan={4} style={{
                            padding: 'var(--s4) var(--s8)',
                            fontSize: 'var(--t-10)',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: 'var(--ink-3)',
                            background: 'var(--bg)',
                            borderTop: '2px solid var(--accent)',
                            borderBottom: '1px solid var(--line)',
                          }}>
                            {t('drilldown.attendance.previousMeasures')}
                          </td>
                        </tr>
                      )}
                    <tr>
                      <td>{a.absence_type || '—'}</td>
                      <td>{formatDate(a.start_date)}</td>
                      <td>{formatDate(a.end_date)}</td>
                      <td style={{ color: 'var(--ink-3)' }}>{a.comment || '—'}</td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </section>
            );
          })()}
        </div>
        );
      })()}

      {/* ── Tab: Journal & Reports ── */}
      {tab === 'journal' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s16)' }}>
          {/* Date range filter */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--s10)', flexWrap: 'wrap',
            padding: 'var(--s8) var(--s12)',
            borderRadius: 'var(--r1)',
            border: '1px solid var(--line)',
            backgroundColor: 'var(--surface)',
          }}>
            <span style={{ fontSize: 'var(--t-12)', fontWeight: 500, color: 'var(--ink-2)' }}>{t('drilldown.journal.period')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s6)' }}>
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('drilldown.journal.from')}</label>
              <input
                type="date"
                value={journalDateFrom}
                onChange={e => setJournalDateFrom(e.target.value)}
                style={{
                  fontSize: 'var(--t-12)', padding: '3px 6px',
                  borderRadius: 'var(--r1)', border: '1px solid var(--line)',
                  color: 'var(--ink-1)', background: 'var(--bg)',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s6)' }}>
              <label style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('drilldown.journal.to')}</label>
              <input
                type="date"
                value={journalDateTo}
                onChange={e => setJournalDateTo(e.target.value)}
                style={{
                  fontSize: 'var(--t-12)', padding: '3px 6px',
                  borderRadius: 'var(--r1)', border: '1px solid var(--line)',
                  color: 'var(--ink-1)', background: 'var(--bg)',
                }}
              />
            </div>
            {(journalDateFrom || journalDateTo) && (
              <>
                <button
                  className="btn btn-sm"
                  onClick={() => { setJournalDateFrom(''); setJournalDateTo(''); }}
                >
                  {t('drilldown.journal.clear')}
                </button>
                <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                  {t('drilldown.journal.entriesCount', { n: journal.filter(e => {
                    const d = (e.entry_date ?? '').slice(0, 10);
                    if (journalDateFrom && d < journalDateFrom) return false;
                    if (journalDateTo && d > journalDateTo) return false;
                    return true;
                  }).length, total: journal.length })}
                </span>
              </>
            )}
          </div>

          {/* AI Journal Synthesis */}
          {measureId && (
            <AIPanel
              title={t('drilldown.journal.aiSynthesisTitle')}
              buttonLabel={t('drilldown.journal.aiSynthesisButton')}
              hint={(journalDateFrom || journalDateTo)
                ? t('drilldown.journal.aiPeriodHint', { from: journalDateFrom || '…', to: journalDateTo || '…' })
                : t('drilldown.journal.aiPeriodDefault')}
              onGenerate={async () => {
                const result = await api.journalSynthesis(measureId, {
                  dateFrom: journalDateFrom || undefined,
                  dateTo: journalDateTo || undefined,
                }) as any;
                return result.synthesis;
              }}
            />
          )}

          {/* Journal entries */}
          {(() => {
            const filteredJournal = journal.filter(e => {
              const d = (e.entry_date ?? '').slice(0, 10);
              if (journalDateFrom && d < journalDateFrom) return false;
              if (journalDateTo && d > journalDateTo) return false;
              return true;
            });
            return (
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s8)' }}>
              <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.journal.title')}</h3>
              <span className="stat-pill">{t('drilldown.journal.entryCount', { n: filteredJournal.length })}</span>
            </div>
            {filteredJournal.length === 0 ? (
              <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>
                {journal.length === 0 ? t('drilldown.journal.emptyAll') : t('drilldown.journal.emptyPeriod')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
                {filteredJournal.map((entry: any) => (
                  <div key={entry.id} style={{
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r1)',
                    padding: 'var(--s8) var(--s12)',
                    backgroundColor: 'var(--surface)',
                  }}>
                    {/* Metadata row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s4)', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 'var(--t-12)', color: 'var(--ink-1)' }}>
                        {formatDate(entry.entry_date)}
                      </span>
                      <span className="stat-pill">{entry.entry_type_label || t('drilldown.journal.entryTypeFallback', { type: entry.entry_type })}</span>
                      {entry.context === 'attendance' && (
                        <span className="badge badge-sm" title={t('drilldown.journal.attendanceCommentTitle')}>{t('drilldown.journal.attendanceBadge')}</span>
                      )}
                      {entry.measure_type && (
                        <span className="badge badge-sm">{entry.measure_type}</span>
                      )}
                      {entry.author_first_name && (
                        <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                          {t('drilldown.journal.byAuthor', { name: `${entry.author_first_name} ${entry.author_last_name}` })}
                        </span>
                      )}
                    </div>
                    {/* Content */}
                    <div style={{ fontSize: 'var(--t-13)', lineHeight: 1.6, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
                      {entry.content || '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
            );
          })()}

          {/* Reports */}
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s8)' }}>
              <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.journal.reportsTitle')}</h3>
              <span className="stat-pill">{t('drilldown.journal.reportsCount', { n: reports.length })}</span>
            </div>
            {reports.length === 0 ? (
              <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('drilldown.journal.noReports')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
                {reports.map((r: any) => (
                  <details key={r.id} style={{
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r1)',
                    backgroundColor: 'var(--surface)',
                    overflow: 'hidden',
                  }}>
                    <summary style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--s8)',
                      padding: 'var(--s8) var(--s12)',
                      cursor: 'pointer',
                      fontSize: 'var(--t-12)',
                      userSelect: 'none',
                    }}>
                      <span style={{ fontWeight: 600 }}>
                        {formatDate(r.period_start)} — {formatDate(r.period_end)}
                      </span>
                      <span className="badge badge-sm">{r.measure_type}</span>
                      <span className={`badge badge-sm ${r.status === 'validated' || r.status === 'submitted' ? 'badge-ok' : ''}`}>
                        {r.status}
                      </span>
                      {r.conclusion_label && <span style={{ color: 'var(--ink-3)' }}>{r.conclusion_label}</span>}
                      {r.author_name && (
                        <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>{t('drilldown.journal.byAuthor', { name: r.author_name })}</span>
                      )}
                      {r.content && (
                        <span style={{ color: 'var(--ink-3)', marginLeft: 'auto', fontSize: 'var(--t-11)' }}>
                          {t('drilldown.journal.charCount', { n: r.content.length })}
                        </span>
                      )}
                    </summary>
                    {r.content && (
                      <div style={{
                        padding: 'var(--s8) var(--s12)',
                        borderTop: '1px solid var(--line)',
                        fontSize: 'var(--t-13)',
                        lineHeight: 1.6,
                        color: 'var(--ink-2)',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {r.content}
                        {r.next_steps && (
                          <div style={{ marginTop: 'var(--s8)', paddingTop: 'var(--s8)', borderTop: '1px solid var(--line)' }}>
                            <span style={{ fontWeight: 600, color: 'var(--ink-1)', fontSize: 'var(--t-12)' }}>{t('drilldown.journal.nextSteps')} </span>
                            {r.next_steps}
                          </div>
                        )}
                        {r.objectives_achieved && (
                          <div style={{ marginTop: 'var(--s4)' }}>
                            <span style={{ fontWeight: 600, color: 'var(--ink-1)', fontSize: 'var(--t-12)' }}>{t('drilldown.objectives.title')} </span>
                            {r.objectives_achieved}
                          </div>
                        )}
                      </div>
                    )}
                    {!r.content && (
                      <div style={{ padding: 'var(--s8) var(--s12)', borderTop: '1px solid var(--line)', color: 'var(--ink-3)', fontSize: 'var(--t-12)' }}>
                        {t('drilldown.journal.noContent')}
                      </div>
                    )}
                  </details>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Tab: Timeline ── */}
      {tab === 'timeline' && (() => {
        const ICONS: Record<string, { icon: string; color: string; label: string }> = {
          attendance: { icon: '●', color: 'var(--accent)', label: t('drilldown.timeline.icon.attendance') },
          first_atelier: { icon: '★', color: 'var(--ok)', label: t('drilldown.timeline.icon.firstWorkshop') },
          journal: { icon: '✎', color: 'var(--ink-2)', label: t('drilldown.timeline.icon.journal') },
          absence: { icon: '✕', color: 'var(--err)', label: t('drilldown.timeline.icon.absence') },
          vacation: { icon: '☀', color: 'var(--warn)', label: t('drilldown.timeline.icon.vacation') },
          measure_start: { icon: '▶', color: 'var(--ok)', label: t('drilldown.timeline.icon.measureStart') },
          measure_end: { icon: '■', color: 'var(--err)', label: t('drilldown.timeline.icon.measureEnd') },
          report: { icon: '📄', color: 'var(--accent)', label: t('drilldown.timeline.icon.report') },
          bsp: { icon: '📋', color: 'var(--accent)', label: 'BSP' },
          placement: { icon: '🏢', color: 'var(--accent-mid)', label: t('drilldown.timeline.icon.placement') },
          appointment: { icon: '📅', color: 'var(--accent)', label: t('drilldown.timeline.icon.appointment') },
          appointment_future: { icon: '📅', color: 'var(--ok)', label: t('drilldown.timeline.icon.appointmentFuture') },
        };
        const defaultIcon = { icon: '●', color: 'var(--ink-3)', label: '?' };

        // Group events by ISO week for week separators
        const getWeekKey = (dateStr: string) => {
          const d = new Date(dateStr);
          // ISO week: Monday-based
          const day = d.getDay() || 7;
          d.setDate(d.getDate() + 4 - day);
          const yearStart = new Date(d.getFullYear(), 0, 1);
          const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
          return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
        };

        const weekLabel = (dateStr: string) => {
          const d = new Date(dateStr);
          // Find Monday of this week
          const day = d.getDay() || 7;
          const monday = new Date(d);
          monday.setDate(d.getDate() - day + 1);
          const friday = new Date(monday);
          friday.setDate(monday.getDate() + 4);
          const month = friday.toLocaleDateString(fd.locale, { month: 'short' });
          return t('drilldown.timeline.weekRange', { start: monday.getDate(), end: friday.getDate(), month, year: friday.getFullYear() });
        };

        let lastWeek = '';

        return (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s12)', marginBottom: 'var(--s8)' }}>
            <h3 style={{ fontSize: 'var(--t-13)', fontWeight: 600, color: 'var(--ink-1)', margin: 0 }}>{t('drilldown.timeline.title')}</h3>
            <span className="stat-pill">{t('drilldown.timeline.eventCount', { n: timeline.length })}</span>
          </div>
          {timeline.length === 0 ? (
            <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('drilldown.timeline.noEvents')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {timeline.map((ev: any, i: number) => {
                const ic = ICONS[ev.event_type] || defaultIcon;
                const weekKey = ev.event_date ? getWeekKey(ev.event_date) : '';
                const showWeekSep = weekKey !== lastWeek;
                if (weekKey) lastWeek = weekKey;

                return (
                  <div key={i}>
                    {/* Week separator */}
                    {showWeekSep && ev.event_date && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 'var(--s8)',
                        padding: 'var(--s8) 0 var(--s4)',
                        marginTop: i > 0 ? 'var(--s4)' : 0,
                        borderTop: i > 0 ? '1px solid var(--line-strong)' : 'none',
                      }}>
                        <span style={{ fontSize: 'var(--t-11)', fontWeight: 600, color: 'var(--ink-1)', whiteSpace: 'nowrap' }}>
                          {t('drilldown.timeline.weekOf', { range: weekLabel(ev.event_date) })}
                        </span>
                        <div style={{ flex: 1, height: 1, backgroundColor: 'var(--line)' }} />
                      </div>
                    )}

                    {/* Event row */}
                    <div style={{
                      display: 'flex', gap: 'var(--s8)', padding: '3px 0',
                      fontSize: 'var(--t-12)',
                      alignItems: 'flex-start',
                      ...(ev.event_type === 'appointment_future' ? { backgroundColor: 'color-mix(in srgb, var(--ok) 8%, var(--surface))', borderRadius: 'var(--r1)', padding: '3px var(--s4)' } : {}),
                    }}>
                      {/* Date */}
                      <span style={{ width: 70, flexShrink: 0, color: 'var(--ink-3)', fontWeight: 500, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-11)' }}>
                        {ev.event_date?.slice(5) || ''}
                      </span>

                      {/* Icon */}
                      <span style={{ width: 16, flexShrink: 0, textAlign: 'center', color: ic.color, fontSize: ev.event_type === 'first_atelier' ? 12 : 10 }}>
                        {ic.icon}
                      </span>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Attendance: compact single line */}
                        {ev.event_type === 'attendance' && (
                          <span>
                            <span style={{ color: 'var(--ink-2)' }}>{ev.title}</span>
                            <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>
                            {ev.extra?.first_time && (
                              <span style={{ color: 'var(--ok)', fontWeight: 600, marginLeft: 'var(--s4)' }}>
                                {t('drilldown.timeline.firstTime', { list: ev.extra.first_time.join(', ') })}
                              </span>
                            )}
                          </span>
                        )}

                        {/* Placement (external stage) — compact like attendance */}
                        {ev.event_type === 'placement' && (
                          <span>
                            <span style={{ fontWeight: 500, color: 'var(--ink-2)' }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}

                        {/* First atelier */}
                        {ev.event_type === 'first_atelier' && (
                          <span style={{ color: 'var(--ok)', fontWeight: 600 }}>{ev.title}</span>
                        )}

                        {/* Journal: expandable */}
                        {ev.event_type === 'journal' && (
                          ev.detail ? (
                            <details style={{ cursor: 'pointer' }}>
                              <summary style={{ display: 'flex', gap: 'var(--s4)', alignItems: 'baseline' }}>
                                <span className="stat-pill" style={{ fontSize: 'var(--t-10)' }}>{ev.title}</span>
                                {ev.extra?.author && <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>{ev.extra.author}</span>}
                              </summary>
                              <div style={{ fontSize: 'var(--t-12)', lineHeight: 1.5, color: 'var(--ink-2)', marginTop: 'var(--s4)', whiteSpace: 'pre-wrap' }}>
                                {ev.detail}
                              </div>
                            </details>
                          ) : (
                            <span>
                              <span className="stat-pill" style={{ fontSize: 'var(--t-10)' }}>{ev.title}</span>
                              {ev.extra?.author && <span style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)', marginLeft: 'var(--s4)' }}>{ev.extra.author}</span>}
                            </span>
                          )
                        )}

                        {/* Absence / Vacation */}
                        {(ev.event_type === 'absence' || ev.event_type === 'vacation') && (
                          <span>
                            <span style={{ fontWeight: 600, color: ev.event_type === 'vacation' ? 'var(--warn)' : 'var(--err)' }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}

                        {/* Measure start/end */}
                        {(ev.event_type === 'measure_start' || ev.event_type === 'measure_end') && (
                          <span>
                            <span style={{ fontWeight: 600, color: ev.event_type === 'measure_start' ? 'var(--ok)' : 'var(--err)' }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}

                        {/* Report */}
                        {ev.event_type === 'report' && (
                          <span>
                            <span style={{ fontWeight: 500 }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}

                        {/* BSP */}
                        {ev.event_type === 'bsp' && (
                          <span>
                            <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}

                        {/* Appointments */}
                        {(ev.event_type === 'appointment' || ev.event_type === 'appointment_future') && (
                          <span>
                            <span style={{ fontWeight: 600, color: ev.event_type === 'appointment_future' ? 'var(--ok)' : 'var(--accent)' }}>{ev.title}</span>
                            {ev.detail && <span style={{ color: 'var(--ink-3)', marginLeft: 'var(--s4)' }}>{ev.detail}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      <AppointmentDetailModal appointment={previewAppointment} onClose={() => setPreviewAppointment(null)} />

      {/* Adjustment modal — opens for whichever mesure id is set */}
      {(() => {
        if (adjustingMeasureId == null) return null;
        const m = measuresData.find((x: any) => x.id === adjustingMeasureId)
          ?? measures.find((x: any) => x.id === adjustingMeasureId);
        if (!m || !person) return null;

        // Pick the plan currently effective for this mesure (or null if none).
        const planForMeasure = plansByMeasure[String(adjustingMeasureId)] ?? null;
        // 4-week avg taux + 30-day presence — computed earlier in the overview render scope.
        // Read fresh from the page state instead of reaching into that scope.
        const last4Real = weeklyHistory.filter((w: any) => !w.is_vacation).slice(-4);
        const tauxAvgLocal = last4Real.length > 0
          ? Math.round(last4Real.reduce((s: number, w: any) => s + (w.hours / 40) * 100, 0) / last4Real.length)
          : null;
        const presAvgLocal = last4Real.length > 0
          ? Math.round(last4Real.reduce((s: number, w: any) => s + (w.presence_pct ?? 0), 0) / last4Real.length)
          : null;

        return (
          <AdjustmentModal
            open
            onClose={() => setAdjustingMeasureId(null)}
            onSaved={() => {
              setAdjustingMeasureId(null);
              setPlanVersion(v => v + 1);
              setCommsRefresh(v => v + 1);
              setTab('communications');
            }}
            ctx={{
              measure_id: m.id,
              participant_id: participantId,
              participant_first_name: person?.first_name ?? null,
              participant_last_name: person?.last_name ?? null,
              type_label: m.subtype_name || m.type_label || null,
              contract_number: m.contract_number ?? null,
              start_date: m.start_date ?? null,
              end_date: m.end_date ?? null,
            }}
            currentPlan={planForMeasure}
            currentTauxPct={tauxAvgLocal}
            presence30dPct={presAvgLocal}
            oaiConseillerName={
              m.contact_first_name ? `${m.contact_first_name} ${m.contact_last_name}` : null
            }
            oaiConseillerEmail={null /* not yet exposed in measures payload */}
          />
        );
      })()}
    </div>
  );
}
